(function (root) {
  'use strict';

  /**
   * Shared UI copy grouped by module + locale.
   * Extend this module when adding new localized strings.
   */
  const app = root.App = root.App || {};
  const existingStrings = app.strings || {};

  const UI_COPY = {
    en: {
      shell: {
        pageTitle: 'Inbox · RFQ Assistant',
        title: 'RFQ Assistant',
        rfqInboxAria: 'RFQ inbox',
        analyticsOpen: 'Open analytics',
        analyticsLabel: 'Analytics',
      },
      login: {
        pageTitle: 'Sign in · Sovai',
        title: 'Sign in to your workspace',
        emailLabel: 'Work email',
        emailPlaceholder: 'you@company.com',
        submit: 'Send sign-in link',
        help: "We'll email you a one-time sign-in link. No password required.",
        devLinkReady: 'Dev link ready:',
        continue: 'Continue to inbox',
        languageToggleAria: 'Choose interface language',
        languageLabel: 'Language',
        languageCzech: 'Čeština',
        languageEnglish: 'English',
        languageCzechShort: 'CS',
        languageEnglishShort: 'EN',
        privacyPolicy: 'Privacy Policy',
        cookieSettings: 'Cookie Settings',
        termsOfUse: 'Terms of Use',
        enterEmail: 'Enter a valid email address.',
        sending: 'Sending sign-in link…',
        checkEmail: 'Check your email for the sign-in link.',
        loginFailed: 'Login failed.',
        errorInviteNotFound: 'Invite not found.',
        errorInviteExpired: 'Invite expired.',
        errorInviteAccepted: 'Invite already accepted.',
        errorInviteEmailMismatch: 'Invite email does not match this login.',
        errorWorkspaceRequired: 'Workspace is required.',
        errorUnauthorizedWorkspace: 'User is not authorized for this workspace.',
      },
      inbox: {
        searchLabel: 'Search inbox',
        searchPlaceholder: 'Search RFQs, parts, materials',
        sortAria: 'Sort RFQs',
        sortReceivedDesc: 'Received (newest)',
        sortReceivedAsc: 'Received (oldest)',
        sortPriority: 'Priority',
        sortStatus: 'Status',
        workspaceRfqs: 'Workspace RFQs',
        summaryDefault: 'All · Received',
        back: 'Inbox',
        review: 'Review',
        filterLabel: 'Filter list',
        filterMultiple: 'Multiple',
        filterClear: 'Clear',
        refresh: 'Refresh inbox',
        demoParsedScroll: 'Auto-scroll parsed data',
        demoAssistantScroll: 'Auto-scroll assistant pane',
        demoReplay: 'Replay demo',
        filterButton: 'Filter RFQs',
        sortButton: 'Sort RFQs',
        statuses: {
          all: 'All',
          new: 'New',
          inProgress: 'In progress',
          pending: 'Pending',
          quoted: 'Quoted',
          won: 'Won',
          lost: 'Lost',
          archived: 'Archived',
        },
        statusAllTitle: 'All except hidden',
        hidden: 'Hidden',
        emptyTitle: 'No RFQs yet',
        emptyBody: 'Connect an email account in Settings or sync your inbox to start reviewing requests for quotation.',
        emptySync: 'Sync inbox',
        emptySettings: 'Open settings',
        settingsOpen: 'Open settings',
        lastActivityTitle: 'Last activity',
        dateToday: 'Today',
        dateYesterday: 'Yesterday',
        filterLabelActive: 'Filtered list ({count})',
        noMessages: 'No messages',
        loadedMessages: 'Loaded {count} messages',
        syncing: 'Syncing inbox…',
        noConnectedAccounts: 'No connected mail accounts',
        refreshed: 'Inbox refreshed',
        syncFailed: 'Sync failed',
        threadCountAria: '{count} messages in thread',
        threadCountTitle: '{count} more emails in this thread',
        threadNewBadge: '{count} new',
        threadNewBadgeOne: '{count} new',
        threadNewBadgeOther: '{count} new',
        updateHiddenFailed: 'Failed to update hidden state',
        autoprocess: {
          queued: 'Queued',
          queuedTitle: 'Queued for auto-processing',
          running: 'Processing',
          runningTitle: 'Auto-processing in progress',
          failed: 'Retry needed',
          failedTitle: 'Auto-processing failed',
        },
        loadError: 'Failed to load inbox.',
        loadErrorToast: 'Failed to load inbox',
        threadLoading: 'Loading thread…',
      },
      preview: {
        emptyTitle: 'No message selected',
        emptyBody: 'Choose an RFQ from the inbox to preview the email, attachments, and request details, then draft your reply in the composer.',
        subjectPlaceholder: 'Select a message',
        needsReview: 'Needs review',
        buyerFallback: 'Buyer',
        from: 'From:',
        to: 'To:',
        recipientsMore: '+{count} more',
        quickReplyAria: 'Quick reply dock',
        attach: 'Attach files',
        inputLabel: 'Reply message',
        inputPlaceholder: 'Type your reply here...',
        openEditor: 'Open Full Editor',
        polish: 'Polish',
        generateQuote: 'Generate Quote',
        minimizeQuickReply: 'Minimize quick reply',
        copied: 'Copied!',
        copyEmailText: 'Copy email text',
        copyFailed: 'Copy failed',
        newMessage: 'New message',
        newMessages: 'New messages',
        attachmentFallback: 'Attachment',
        attachmentFallbackNamed: 'Attachment {index}',
        attachmentsSingle: 'ATTACHMENT (1)',
        attachmentsMultiple: 'ATTACHMENTS ({count})',
        openAttachment: 'Open attachment',
        downloadAttachment: 'Download attachment',
        openAttachmentNamed: 'Open attachment {filename}',
        unknownSize: 'Unknown size',
        providerOpenMissing: 'Open your email provider from the preview once available.',
        send: 'Send',
        draftClarification: 'Draft Clarification',
        threadActions: 'Thread actions',
        moreActions: 'More actions',
        linkToRfq: 'Link to RFQ',
        copyRfqId: 'Copy RFQ ID',
        notRfq: 'Not an RFQ',
        notRfqReason: 'Marked as not an RFQ',
        notRfqConfirmTitle: 'Remove email from app?',
        notRfqConfirmBody: 'This email will be marked as not an RFQ and will stop appearing in the app. The email itself will not be deleted.',
        notRfqConfirmCancel: 'Cancel',
        notRfqConfirmConfirm: 'Remove from app',
        rfqIdUnavailable: 'RFQ ID unavailable',
        rfqIdCopied: 'RFQ ID copied',
        rfqIdCopyFailed: 'Unable to copy RFQ ID',
        hideFromInbox: 'Hide from inbox',
        reportSupport: 'Report problem or improvement',
        messageCountOne: '{count} message',
        messageCountOther: '{count} messages',
        newCountOne: '{count} new message',
        newCountOther: '{count} new messages',
        lastActivity: 'Last activity: {date}',
        noMessageContent: 'No message content.',
        showMore: 'Show more',
        showLess: 'Show less',
        attachmentSummaryOne: '{count} attachment ▾',
        attachmentSummaryOther: '{count} attachments ▾',
        copyToClipboardSuccess: 'Copied email to clipboard.',
        copyToClipboardError: 'Copy failed.',
      },
      composer: {
        title: 'Reply',
        controlsGroup: 'Composer controls',
        showOriginal: 'Show original message',
        hideOriginal: 'Hide original message',
        aiSettings: 'AI settings',
        close: 'Close composer',
        aiAssist: 'AI Assist',
        settingsSummary: 'AI settings summary',
        language: 'Language',
        languageSettings: 'Language settings',
        tone: 'Tone',
        toneSettings: 'Tone settings',
        length: 'Length',
        lengthSettings: 'Length settings',
        signature: 'Signature',
        signatureSettings: 'Signature settings',
        toneLengthSettings: 'Tone and length settings',
        draftMode: 'Draft mode',
        modeClarify: 'Clarify',
        modeQuote: 'Quote',
        draftPlaceholder: 'Write your message...',
        lastNotes: 'Last notes',
        cancel: 'Cancel',
        recommended: 'Recommended',
        noMessageContent: 'No message content available.',
        selectMessageBeforeSend: 'Select a message before sending.',
        startTypingToSend: 'Start typing to enable Send.',
        invalidRecipient: 'Add at least one valid recipient email in To.',
        sendCanceled: 'Send canceled.',
        sendAccountMissing: 'Unable to determine send account.',
        reconnectMailAccount: 'Reconnect your mail account in Settings > Mail accounts, then try sending again.',
        attachmentRequired: 'Upload at least one staged quote attachment before sending.',
        sendFailed: 'Send failed. Please try again.',
        copyFailed: 'Copy failed',
        uploadRfqFirst: 'Upload an RFQ first.',
        draftError: 'Draft error',
        polishNeedText: 'Add text to polish first.',
        polishNeedMoreDetail: 'Add more detail before polishing (≈15 words).',
        historyAiDraft: 'AI draft',
        historyRegenerated: 'Regenerated',
        historyPolished: 'Polished',
        polishStatus400: 'Please add a bit more text to polish.',
        polishStatus404: 'RFQ not found',
        polishStatus408: 'Polish timed out. Try again.',
        polishStatus422: "Couldn't polish without changing meaning. Your text was left unchanged.",
        polishStatus502: 'Polish timed out. Try again.',
        polishGenericError: 'Polish failed. Try again.',
      },
      aiSettings: {
        title: 'AI settings',
        helper: 'Applies on next draft',
        language: 'Language',
        automatic: 'Automatic',
        manualLanguageChoices: 'Manual language choices',
        czech: 'Czech',
        german: 'German',
        english: 'English',
        toneLength: 'Tone & Length',
        tone: 'Tone',
        toneFormal: 'Formal',
        toneNeutral: 'Neutral',
        toneFriendly: 'Friendly',
        length: 'Length',
        lengthConcise: 'Concise',
        lengthMedium: 'Medium',
        lengthDetailed: 'Detailed',
        display: 'Display',
        showStatusChips: 'Show current AI settings',
      },
      signature: {
        appendOptions: 'Append options',
        none: 'None',
        noneHelp: 'No signature will be appended.',
        basicText: 'Basic text',
        basicTextHelp: 'Recommended — clean multi-line signature.',
        customHtml: 'Custom HTML',
        customHtmlHelp: 'Advanced — use sanitized company HTML.',
        details: 'Signature details',
        textHeading: 'Basic text signature',
        textHelper: 'Fill in as much as you want to share.',
        name: 'Name *',
        roleCompany: 'Role / Company',
        phone: 'Phone',
        email: 'Email',
        reset: 'Reset',
        htmlHeading: 'Custom HTML signature',
        htmlHelper: 'Paste your vetted HTML. We’ll sanitize it before use.',
        htmlSource: 'HTML source',
        htmlSave: 'Sanitize & Save',
        preview: 'Preview',
        toastHtmlMissing: 'Custom HTML signature missing. Add it in settings to use your company block.',
        toastDemo: 'Using demo signature. Save your details to personalize it.',
        toastSaved: 'Signature saved.',
        toastCleared: 'Signature cleared.',
        toastSavedClearedHtml: 'Signature saved — cleared custom HTML.',
        toastSanitizedEmpty: 'Signature saved — sanitized HTML had no allowed content.',
      },
      research: {
        title: 'Buyer Insight',
        close: 'Close research details',
        snapshot: 'Snapshot',
        industries: 'Industries',
        materials: 'Materials',
        capabilities: 'Key capabilities',
        certifications: 'Certifications',
        companyDetails: 'Company details',
        riskFlags: 'Risk flags',
        sources: 'Sources',
        selectMessageFirst: 'Select a message with company details first.',
        selectMessageToAnalyze: 'Select a message to analyze.',
        analyzeSender: 'Analyze sender',
        runToView: 'Run research to view details.',
        refresh: 'Refresh',
        copyTalkingPoints: 'Copy talking points',
        noSummaryYet: 'No summary available yet.',
        notAvailable: 'Not available',
        noResearchSummary: 'No research summary yet.',
        copyTalkingPointsSuccess: 'Talking points copied',
        copyTalkingPointsError: 'Unable to copy to clipboard',
        moreLabel: '{count} more {label}',
        fallbackDefaultMessage: 'No credible information found.',
        fallbackBusyTimedOut: 'Service busy or timed out.',
        missingCompanyDetails: 'Missing company details.',
        requestTimedOut: 'Research timed out. Try again.',
        requestFailed: 'Research request failed.',
        requestFailedRetry: 'Research request failed. Please try again.',
        progressResearching: 'Researching: {company} …',
        companyFallback: 'company',
        resultCompanyFallback: 'Company',
        likelyBuyerType: 'Likely buyer type: {type}',
        badgeCached: 'Cached',
        badgeUpdated: 'Updated',
        badgeLive: 'Live',
        badgeFallback: 'Fallback',
        latestFreshToast: 'Latest research is under 24 hours old.',
        refreshUsedToast: 'Refresh already used for this RFQ.',
        topCitedFacts: 'Top cited facts:',
        website: 'Website',
        headquarters: 'HQ',
        employees: 'Employees',
        annualRevenue: 'Annual revenue',
        financialPeriod: 'Financial period',
        shippingRegions: 'Shipping regions',
        sourceFallback: 'Source',
        relativeJustNow: 'just now',
        relativeMinutesAgo: '{count}m ago',
        relativeHoursAgo: '{count}h ago',
        relativeDayAgo: '1 day ago',
        relativeDaysAgo: '{count} days ago',
      },
      related: {
        title: 'RFQs',
        summaryPlaceholder: 'Select a message to view related RFQs.',
        summaryUnavailable: 'Process this RFQ to surface related requests.',
        tabs: {
          duplicates: 'Duplicates',
          company: 'Same company',
          similar: 'Find similar',
          lookup: 'Lookup',
        },
        empty: {
          duplicates: 'No duplicate RFQs detected for this message.',
          company: 'No other RFQs from this company yet.',
          similar: 'No similar RFQs match the selected filters.',
          lookup: 'Start typing to search RFQs across this workspace.',
        },
        compare: 'Compare',
        openEmail: 'Open email',
        quoteNotFound: 'Quote not found',
        quoteLoading: 'Loading quote…',
        quoteRetry: 'Retry quote lookup',
        quoteViewSent: 'View sent quote',
        noSavedTags: 'No saved tags for this RFQ.',
        loadMore: 'Load more',
        materials: 'Materials (comma separated)',
        norms: 'Norms (comma separated)',
        partNumbers: 'Part numbers (comma separated)',
        retry: 'Retry',
        currentRfq: 'Current RFQ',
        comparedRfq: 'Compared RFQ',
        details: 'Details',
        lookupPlaceholder: 'Search company, contact, or subject',
        lookupLoading: 'Searching RFQs…',
        lookupHelper: 'Minimum two characters. Results are scoped to your workspace.',
        lookupMinChars: 'Enter at least two characters to search.',
        unavailableMessage: 'Process this RFQ to surface related requests.',
        messageUnavailable: 'Message unavailable.',
        openMessageFailed: 'Unable to open message.',
        openSentQuoteFailed: 'Unable to open sent quote.',
        quoteLoadFailed: 'Unable to load sent quote.',
        sameCompanyOnly: 'Limit to same company',
        findingSimilar: 'Finding similar RFQs…',
        similarLoadFailed: 'Unable to find similar RFQs.',
        loadingRelated: 'Loading related RFQs…',
        relatedLoadFailed: 'Unable to load related RFQs.',
        unknownCompany: 'Unknown company',
        chipMaterial: 'Material',
        chipNorm: 'Norm',
        chipPart: 'Part',
      },
      notes: {
        deleteEntry: 'Delete workspace note entry',
        scrollLatest: 'Scroll to latest notes',
        latest: 'Latest',
        deleted: 'Note deleted',
        undo: 'Undo',
        concurrentUpdate: 'Someone else updated the workspace note. Refresh to continue.',
        alreadyRemoved: 'Workspace entry was already removed.',
        deleteOwnOnly: 'You can only delete your own entries.',
        restored: 'Note restored',
        undoFailed: "Couldn't undo",
        shareHint: 'Edit and publish to share with your workspace.',
        loadFailed: 'Failed to load notes',
        published: 'Workspace note published',
        publishFailed: 'Publish failed',
        publishing: 'Publishing…',
        appendOnly: 'You can add to the workspace note but not remove existing text.',
        noteTooLong: 'Workspace notes are limited to {count} characters per entry.',
        someone: 'Someone',
        today: 'Today',
        yesterday: 'Yesterday',
        undated: 'Undated',
        sharedBy: 'Shared by {author}',
        relativeJustNow: 'just now',
        relativeMinuteAgo: '1 minute ago',
        relativeMinutesAgo: '{count} minutes ago',
        relativeHourAgo: '1 hour ago',
        relativeHoursAgo: '{count} hours ago',
        relativeDayAgo: '1 day ago',
        relativeDaysAgo: '{count} days ago',
      },
      settings: {
        title: 'Settings',
        close: 'Close settings',
        profile: 'Profile',
        preferences: 'Preferences',
        appearance: 'Appearance',
        signedInAs: 'Signed in as',
        signOut: 'Sign out',
        mailAccounts: 'Mail accounts',
        defaultOutbound: 'Default outbound account',
        defaultNone: 'No default (require explicit account)',
        save: 'Save',
        noAccounts: 'No accounts connected yet.',
        gmail: 'Gmail',
        outlook: 'Outlook',
        imap: 'IMAP',
        connect: 'Connect',
        sync: 'Sync',
        disconnect: 'Disconnect',
        you: 'You',
        remove: 'Remove',
        pending: 'Pending',
        users: 'Users',
        invites: 'Invites',
        invite: 'Invite',
        language: 'Frontend language',
        languageEnglish: 'English',
        languageCzech: 'Czech',
        interfaceSize: 'Interface size',
        compact: 'Compact',
        defaultSize: 'Default',
        large: 'Large',
        classic: 'Classic',
        warm: 'Warm',
        calculationDefaults: 'Calculation defaults',
      },
      assistant: {
        collapseAssistant: 'Collapse Smart Overview',
        expandAssistant: 'Expand Smart Overview',
        ariaLabel: 'Smart Overview',
        title: 'RFQ Overview',
        paneActions: 'Smart Overview pane actions',
        expandPane: 'Expand Smart Overview pane',
        collapsePane: 'Collapse Smart Overview pane',
        expandTitle: 'Expand',
        collapseTitle: 'Collapse',
        closePane: 'Close Smart Overview pane',
        closeTitle: 'Close',
        readyTitle: 'Smart Overview is ready',
        readyBody: 'Select a message to see summaries, parsed parts, risk signals, and more.',
        capabilitiesAria: 'Smart Overview capabilities',
        cardSummary: 'Summary',
        cardBuyerIntent: 'RFQ profile',
        cardRiskFlags: 'Risk flags',
        cardParsedData: 'Parsed data',
      },
      rfqOverview: {
        selectMessage: 'Select a message',
        from: 'From:',
        due: 'Due',
        unassigned: 'Unassigned',
        assignToMe: 'Assign to me',
        assignedToMe: 'Assigned to me',
        loadingTeammates: 'Loading teammates...',
        buyerIntent: 'RFQ profile',
        priorityScore: 'Queue priority',
        intentConfidence: 'Confidence: {score}%.',
        cellEditedByAt: 'Edited by {name} · {time}',
        cellEditedBy: 'Edited by {name}',
        cellEditedAt: 'Edited · {time}',
        cellEditedManual: 'Manually edited',
        priorityRankOf: 'of {total}',
        priorityBusinessScore: 'Priority score: {score}/100',
        quoteReady: 'Quote ready',
        clarificationNeeded: 'Clarification needed',
        clarificationOptional: 'Clarification optional',
        clarificationSuggested: 'Clarification suggested',
        openQuoteDraft: 'Open quote draft',
        openClarifyingDraft: 'Open clarifying draft',
        dueTodayTitle: 'Due today',
        dueInDaysTitle: 'Due in {count}d',
        overdueByDaysTitle: 'Overdue by {count}d',
        showRiskRationale: 'Show risk rationale',
        why: 'Why?',
        reasonMaterial: 'Material specification missing',
        reasonQuantity: 'Required quantity missing',
        reasonDeadline: 'Deadline missing',
        reasonSpec: 'Tolerance or surface finish missing',
        reasonSpecWithFields: 'Missing {fields}',
        reasonNorm: 'Applicable norm missing',
        reasonFallback: 'Clarifying data missing',
        reasonWithPart: '{reason} ({part})',
        reasonWithCount: '{reason} ({count} items)',
        fieldDescription: 'part name',
        fieldPartNo: 'part number',
        fieldTolerance: 'tolerance',
        fieldHeatTreatment: 'heat treatment',
        fieldSurfaceFinish: 'surface finish',
        fieldConjunction: ' and ',
        summary: 'Summary',
        summaryLanguageBadgeAria: 'Summary language mismatch',
        clarifyingLanguageBadgeAria: 'Clarification language mismatch',
        intentLanguageBadgeAria: 'Buyer intent language mismatch',
        insightLanguageTooltipSummary: 'Summary is stored in {sourceLanguage}. Click to regenerate it in {targetLanguage}.',
        insightLanguageTooltipClarifying: 'Clarifications are stored in {sourceLanguage}. Click to regenerate them in {targetLanguage}.',
        insightLanguageTooltipIntent: 'Buyer intent is stored in {sourceLanguage}. Click to regenerate it in {targetLanguage}.',
        insightLanguageLoading: '{language}...',
        languageNameEn: 'English',
        languageNameCs: 'Czech',
        summaryRefreshFailed: 'Unable to regenerate the summary.',
        clarifyingRefreshFailed: 'Unable to regenerate clarifications.',
        intentRefreshFailed: 'Unable to regenerate buyer intent.',
        loadingSummary: 'Loading summary...',
        summaryUnavailable: 'Summary unavailable.',
        noSummaryAvailable: 'No summary available.',
        selectMessageSummary: 'Select a message to view summary.',
        idle: 'idle',
        showMore: 'Show more',
        related: 'Related',
        viewAllRelatedRfqs: 'View all related RFQs',
        relatedMessage: 'Related RFQ',
        unknownSender: 'Unknown sender',
        relationDuplicate: 'Duplicate',
        relationSameSender: 'Same Sender',
        relationSimilar: 'Similar',
        relationRelated: 'Related',
        relatedMessageUnavailable: 'Unable to open related message. See Related RFQs details below.',
        relatedMessageNotLoaded: 'Related message was not loaded in inbox. See Related RFQs details below.',
        lineItemsOne: '{count} line item',
        lineItemsOther: '{count} line items',
        materialsOne: '{count} material',
        materialsOther: '{count} materials',
        parsedData: 'Parsed Data',
        showParsedMappingDetails: 'Show parsed mapping details',
        export: 'Export',
        exportXlsx: 'Export XLSX',
        exportCsv: 'Export CSV',
        exportMkset: 'Export for MK SET',
        profileFields: 'Client fields',
        allDetected: 'All detected data',
        viewToggleProfile: 'MK SET',
        viewToggleAll: 'All',
        exportGroupMkset: 'MK SET export',
        exportGroupMksetAria: 'About MK SET export',
        exportGroupMksetInfo: 'Three positional columns for MK SET import (quantity, description, part number). No header row.',
        exportGroupComplete: 'Complete export',
        exportGroupCompleteAria: 'About complete export',
        exportGroupCompleteInfo: 'All columns shown in the current parsed table view (MK SET fields or all detected data).',
        exportFull: 'Full canonical export',
        scrollMoreColumns: 'Scroll horizontally for more columns',
        viewAllRows: 'View all rows',
        viewAllRowsCount: 'View all {count} rows',
        mappedSummary: '{mapped}/{total} mapped',
        readMore: 'Read more',
        readLess: 'Read less',
      },
      review: {
        markRfq: 'Mark RFQ',
        markRfqTooltip: 'Add this message to the RFQ inbox and automatically link it to an existing RFQ when applicable.',
        linkRfq: 'Link RFQ',
        linkRfqTooltip: 'Manually link this message to an existing RFQ.',
        ignore: 'Not RFQ',
        ignoreTooltip: 'Remove this message from RFQ Assistant (it will remain in your inbox).',
        linkExistingRfq: 'Link to existing RFQ',
        close: 'Close',
        searchPlaceholder: 'Search by company, contact, or subject',
        searchEmpty: 'Type at least 2 characters to search.',
        quickActions: 'Quick actions',
        expandQuickReply: 'Expand quick reply',
        selectRfqToLink: 'Select an RFQ to link.',
        linkFailed: 'Failed to link message',
        classificationFailed: 'Failed to update classification',
      },
      notesUi: {
        title: 'Notes',
        scope: 'Notes scope',
        private: 'Private',
        workspace: 'Workspace',
        privateNote: 'Private note',
        privatePlaceholder: "Write notes for yourself. They'll stay private to you.",
        workspaceNote: 'Workspace note',
        workspacePlaceholder: 'Share a note with everyone in this workspace.',
        refreshNotes: 'Refresh notes',
        publish: 'Publish',
      },
      draftSettings: {
        close: 'Close',
        save: 'Save',
      },
      analytics: {
        title: 'Analytics',
        close: 'Close analytics',
        moreComing: 'More analytics coming soon',
      },
      sendAccountPicker: {
        eyebrow: 'New send',
        title: 'Choose sender account',
        close: 'Close account picker',
        subtitle: 'Select the account to send this message from.',
        options: 'Send account options',
        cancel: 'Cancel',
        send: 'Send',
      },
      settingsUi: {
        languageHelp: 'Choose the language used across the app interface.',
        languageSelectAria: 'Frontend language',
        displayNameLabel: 'Display name',
        displayNamePlaceholder: 'How teammates should see you',
        saveDisplayName: 'Save name',
        savingDisplayName: 'Saving display name…',
        displayNameSaved: 'Display name saved.',
        displayNameSaveFailed: 'Unable to save display name.',
        themeHelp: 'Choose between a classic white workspace and a warmer canvas.',
        interfaceSizeHelp: 'Adjust the overall interface scale for readability.',
        interfaceSizeSelectAria: 'Interface size',
        applyingLanguage: 'Applying language…',
        unknownAccount: 'unknown',
        lastSync: 'Last sync {date}',
        confirmDisconnect: 'Disconnect {email}?',
        disconnecting: 'Disconnecting account…',
        accountDisconnected: 'Account disconnected.',
        disconnectFailed: 'Unable to disconnect account.',
        confirmRemoveMember: 'Remove {userId} from this workspace?',
        removingUser: 'Removing user…',
        userRemoved: 'User removed.',
        removeUserFailed: 'Unable to remove user.',
        expiresAt: 'Expires {date}',
        adminLabel: 'Admin',
        memberLabel: 'Member',
        openConsent: 'Opening {provider} consent…',
        oauthStartFailed: 'Unable to start {provider} OAuth.',
        connectingImap: 'Connecting IMAP account…',
        imapConnected: 'IMAP account connected.',
        imapConnectFailed: 'Unable to connect IMAP account.',
        noProviderAccounts: 'No {provider} accounts to sync.',
        syncingProvider: 'Syncing {provider}…',
        syncedProviderSummary: 'Synced {provider}: {inserted} new · {updated} updated',
        syncProviderFailed: 'Unable to sync {provider}.',
        savingDefaultOutbound: 'Saving default outbound account…',
        defaultOutboundSaved: 'Default outbound account saved.',
        defaultOutboundSaveFailed: 'Unable to save default outbound account.',
        enterInviteEmail: 'Enter an email to invite.',
        sendingInvite: 'Sending invite…',
        inviteCreated: 'Invite created.',
        inviteCreatedWithLink: 'Invite created. Link: {link}',
        inviteFailed: 'Unable to create invite.',
        resendInvite: 'Resend',
        confirmResendInvite: 'Resend invite email to {email}?',
        resendingInvite: 'Resending invite…',
        inviteResent: 'Invite resent.',
        resendInviteFailed: 'Unable to resend invite.',
        loadMembersFailed: 'Unable to load members.',
        loadInvitesFailed: 'Unable to load invites.',
        loadMailAccountsFailed: 'Unable to load mail accounts.',
        mailSectionGmailAria: 'Gmail accounts',
        mailSectionOutlookAria: 'Outlook accounts',
        mailSectionImapAria: 'IMAP accounts',
        syncGmail: 'Sync Gmail',
        syncOutlook: 'Sync Outlook',
        syncImap: 'Sync IMAP',
        noGmailAccounts: 'No Gmail accounts connected.',
        noOutlookAccounts: 'No Outlook accounts connected.',
        noImapAccounts: 'No IMAP accounts connected.',
        usersHint: 'Admins can invite teammates and manage access.',
        inviteEmailPlaceholder: 'name@example.com',
        inviteRole: 'Invite role',
        roleMember: 'Member',
        roleAdmin: 'Admin',
        sendInvite: 'Send invite',
        pendingInvites: 'Pending invites',
        noPendingInvites: 'No pending invites.',
        workspaceMembers: 'Workspace members',
        noMembersFound: 'No members found.',
        calculationSettingsTitle: 'Calculation settings',
        calculationSettingsSubtitle: 'Workspace defaults used by new calculation previews.',
        closeCalculationSettings: 'Close calculation settings',
        calculationDefaultsHelp: 'Used when a new Calculation preview is created. Existing worksheets keep their saved values.',
        defaultHourlyRate: 'Default hourly rate',
        defaultMarkup: 'Default markup (%)',
        defaultSetupCost: 'Default setup cost',
        currency: 'Currency',
        notSet: 'Not set',
        calculationReadOnly: 'Only workspace admins can change calculation defaults.',
        saveCalculationDefaults: 'Save defaults',
        calculationDisclaimer: 'Calculation previews are indicative and require human review.',
        calculationLoadFailed: 'Unable to load calculation defaults.',
        calculationInvalidNumber: '{label} must be a valid non-negative number.',
        calculationInvalidCurrency: 'Currency must be a three-letter code.',
        calculationValidationFailed: 'Check the calculation defaults.',
        savingCalculationDefaults: 'Saving calculation defaults…',
        calculationDefaultsSaved: 'Calculation defaults saved.',
        calculationSaveFailed: 'Unable to save calculation defaults.',
        materialDefaultsTitle: 'Material defaults',
        materialDefaultsHelp: 'Used to prefill material cost in new Calculation previews. These are workspace defaults, not live market prices.',
        materialDefaultsMaterialColumn: 'Material',
        materialDefaultsCostColumn: 'Cost / item',
        materialDefaultsCurrencyColumn: 'Currency',
        materialDefaultsNoteColumn: 'Note',
        materialDefaultsActionsColumn: 'Actions',
        materialDefaultsReadOnly: 'Only workspace admins can change material defaults.',
        materialDefaultsAddTitle: 'Add material default',
        materialDefaultsEditTitle: 'Edit material default',
        materialDefaultsMaterialField: 'Material',
        materialDefaultsCostField: 'Cost / item',
        materialDefaultsCurrencyField: 'Currency',
        materialDefaultsNoteField: 'Note',
        materialDefaultsNamePlaceholder: 'EN AW-6082',
        materialDefaultsCostPlaceholder: '180.50',
        materialDefaultsNotePlaceholder: 'Optional note for buyers or estimators',
        materialDefaultsEmpty: 'No active material defaults yet.',
        materialDefaultsSave: 'Save default',
        materialDefaultsSaveChanges: 'Save changes',
        materialDefaultsDeactivate: 'Deactivate',
        materialDefaultsLoadFailed: 'Unable to load material defaults.',
        materialDefaultsNameRequired: 'Material name is required.',
        materialDefaultsInvalidCurrency: 'Currency must be a three-letter code.',
        materialDefaultsValidationFailed: 'Check the material default fields.',
        materialDefaultsSaving: 'Creating material default…',
        materialDefaultsSavingChanges: 'Saving material default…',
        materialDefaultsSaved: 'Material default saved.',
        materialDefaultsUpdated: 'Material default updated.',
        materialDefaultsSaveFailed: 'Unable to save material default.',
        materialDefaultsConfirmDeactivate: 'Deactivate {material}?',
        materialDefaultsDeactivating: 'Deactivating material default…',
        materialDefaultsDeactivated: 'Material default deactivated.',
        materialDefaultsDeleteFailed: 'Unable to deactivate material default.',
        quoteBrandingTitle: 'Quote document branding',
        quoteBrandingHelp: 'Used when a branded PDF quote document is generated.',
        quoteBrandCompanyName: 'Company display name',
        quoteBrandCompanyNamePlaceholder: 'Howda Engineering',
        quoteBrandValidityDays: 'Default validity period',
        quoteBrandLogo: 'Logo',
        quoteBrandLogoChoose: 'Choose logo',
        quoteBrandLogoNoFile: 'No file selected',
        quoteBrandLogoDropHint: 'or drag PNG/JPG here',
        quoteBrandLogoCurrent: 'Current: {filename}',
        quoteBrandLogoMissing: 'No logo uploaded.',
        quoteBrandLogoRemove: 'Remove logo',
        quoteBrandLogoRemoveFailed: 'Unable to remove the logo.',
        quoteBrandPaymentTerms: 'Default payment terms',
        quoteBrandPaymentTermsPlaceholder: 'Payment due within 14 days',
        quoteBrandDeliveryTerms: 'Default delivery terms',
        quoteBrandDeliveryTermsPlaceholder: 'Delivery according to confirmed schedule',
        quoteBrandFooterNote: 'Footer / legal note',
        quoteBrandFooterNotePlaceholder: 'Prices are subject to final confirmation.',
        quoteBrandCompanyAddress: 'Company address',
        quoteBrandCompanyAddressPlaceholder: 'Industrial 12, 619 00 Brno',
        quoteBrandCompanyEmail: 'Company e-mail',
        quoteBrandCompanyEmailPlaceholder: 'sales@company.example',
        quoteBrandCompanyPhone: 'Company phone',
        quoteBrandCompanyPhonePlaceholder: '+420 123 456 789',
        quoteBrandCompanyRegId: 'Company / VAT ID',
        quoteBrandCompanyRegIdPlaceholder: 'Company ID 12345678 · VAT ID CZ12345678',
        quoteBrandShowBreakdown: 'Show pricing breakdown in the PDF',
        quoteBrandShowMarkup: 'Show markup as a separate breakdown line',
        quoteBrandIncomplete: 'Company display name is not set — generated PDFs will use a workspace fallback name.',
        quoteBrandingSaveHelp: 'Logo and document wording are saved for future quote PDFs.',
        saveQuoteBranding: 'Save branding',
      },
      calculationPreview: {
        open: 'Calculation preview',
        title: 'Calculation preview',
        indicative: 'Indicative',
        helper: 'Indicative calculation based on extracted data and configured rates. Review all inputs before preparing a quotation.',
        expand: 'Expand calculation preview',
        expandTitle: 'Expand',
        collapse: 'Collapse calculation preview',
        collapseTitle: 'Collapse',
        close: 'Close calculation preview',
        loading: 'Loading calculation…',
        summary: 'Worksheet summary',
        currency: 'Currency',
        currencyNoConversion: 'Changing the currency updates the currency label only; entered amounts are not converted.',
        indicativeTotal: 'Indicative total',
        reviewDisclaimer: 'Indicative calculation — requires human review.',
        reload: 'Reload',
        markReviewed: 'Mark as reviewed',
        suggestStock: 'Suggest stock size',
        stockSuggestionTitle: 'Stock suggestion',
        refreshStockSuggestion: 'Refresh suggestion',
        stockSuggestionLoading: 'Checking CAD geometry…',
        recommendedStockOption: 'Recommended option',
        recommendedStockOptionShort: 'Recommended',
        selectedStockOption: 'Selected option',
        stockOption: 'Option',
        otherStockOptions: 'Other options',
        otherStockOptionsCount: 'Other options: {count}',
        applyStockOption: 'Select',
        tightStockOption: 'Tight stock',
        roundedStockOption: 'Rounded size',
        conservativeReserveOption: 'Conservative reserve',
        stockPreviewTitle: 'Envelope preview',
        stockPreviewHelper: 'Envelope preview, not exact geometry',
        stockPreviewUnavailable: 'Preview unavailable',
        envelopeVolume: 'Envelope volume',
        finishedVolumeShort: 'Finished',
        stockVolumeShort: 'Stock',
        stockGeometry: 'Stock geometry',
        stockDelta: 'Delta',
        finishedVolume: 'Finished volume',
        stockVolume: 'Stock volume',
        addedEnvelopeVolume: 'Added envelope',
        stockBasis: 'Basis',
        stockBasisTooltip: 'Show stock suggestion basis',
        stockRule: 'Rule',
        stockConfidence: 'Confidence',
        stockRuleMillingBlockDefault: 'Workspace milling allowance',
        stockRuleMillingBlockConservative: 'Conservative milling reserve',
        stockRuleTurningBarDefault: 'Workspace turning allowance',
        stockRuleSheetProfileDefault: 'Workspace sheet/profile handling',
        stockConfidenceHigh: 'High confidence',
        stockConfidenceMedium: 'Medium confidence',
        stockConfidenceLow: 'Low confidence',
        stockConfidenceReview: 'Review',
        stockProcessMilling: 'Milling',
        stockProcessTurning: 'Turning',
        stockProcessSheetProfile: 'Sheet/profile',
        stockProcessUnknown: 'Unknown',
        finishedEnvelope: 'Finished envelope',
        suggestedStock: 'Suggested stock',
        stockAllowance: 'Allowance',
        perSide: 'side',
        processType: 'Process',
        stockSources: 'Source',
        applyStockSuggestion: 'Select recommendation',
        stockSelectedAction: 'Selected',
        stockApplied: 'Selected for calculation',
        stockSuggesting: 'Suggesting stock sizes…',
        stockSuggested: 'Stock suggestions ready for review.',
        stockSuggestFailed: 'Unable to suggest stock sizes.',
        stockWarningMissingCad: 'No CAD attachment was found for this item.',
        stockWarningGeometryUnavailable: 'CAD geometry was unavailable.',
        stockWarningAssembly: 'CAD appears to be an assembly or multi-part file.',
        stockWarningUnits: 'CAD units are unknown.',
        stockWarningSheet: 'Sheet/profile stock requires manual review.',
        stockWarningGeneric: 'Review this stock suggestion.',
        sourceExtracted: 'Extracted',
        sourceDefault: 'Workspace default',
        sourceMaterialDefault: 'Workspace material default',
        sourceManual: 'Manual',
        sourceSystem: 'System default',
        unsaved: 'Unsaved changes',
        reset: 'Reset',
        material: 'Material',
        operations: 'Operations',
        external: 'External processing',
        setup: 'Setup',
        other: 'Other',
        markup: 'Markup',
        operationName: 'Operation',
        minutesPerUnit: 'Minutes / unit',
        hourlyRate: 'Hourly rate',
        removeOperation: 'Remove operation',
        externalName: 'External process',
        costPerUnit: 'Cost / unit',
        removeExternal: 'Remove external process',
        item: 'Item',
        quantityShort: 'Qty',
        complete: 'Complete',
        incomplete: 'Incomplete',
        quantity: 'Quantity',
        heatTreatment: 'Heat treatment',
        surfaceFinish: 'Surface finish',
        process: 'Process',
        materialCostUnit: 'Material cost / unit',
        setupCost: 'Setup cost / batch',
        otherCost: 'Other cost / batch',
        markupPercent: 'Markup (%)',
        internalOperations: 'Internal operations',
        addOperation: 'Add operation',
        noInternalOperations: 'No internal operation cost applies',
        externalProcessing: 'External processing',
        addExternal: 'Add external process',
        reviewed: 'Reviewed',
        applyLatest: 'Apply latest extracted data',
        counts: '{total} items · {complete} complete · {incomplete} incomplete',
        excludesIncomplete: 'Total excludes {count} incomplete item(s).',
        allIncluded: 'All worksheet items are included.',
        sourceStale: 'Extracted RFQ data changed. Apply the latest extracted data before continuing.',
        conflict: 'Someone else updated this worksheet. Reload before continuing.',
        saving: 'Saving…',
        saved: 'Saved',
        saveConflict: 'Save conflict',
        saveFailed: 'Unable to save calculation.',
        loadFailed: 'Unable to load calculation preview.',
        reviewing: 'Marking reviewed…',
        reviewedByHuman: 'Reviewed by a human',
        reviewFailed: 'Unable to mark reviewed.',
        issueQuantity: 'Quantity must be greater than zero.',
        issueOperations: 'Add an internal operation or mark it not applicable.',
        issueOperationName: 'Operation name is required.',
        issueExternalName: 'External process name is required.',
        issueMaterialMissing: 'Material specification is missing from the RFQ.',
        issueHeatMissing: 'No heat-treatment requirement was extracted.',
        issueFinishMissing: 'No surface-finish requirement was extracted.',
        issueQuantityChanged: 'Calculation quantity differs from the extracted quantity.',
        issueZeroMaterial: 'Material cost is explicitly set to zero.',
        issueRequired: 'Required calculation input is missing.',
        issueNonNegative: 'Enter a valid non-negative number.',
        issueGeneric: 'Check this input.',
        quoteRequiresReview: 'Complete and review the calculation preview before generating a quote.',
      },
      composerAttachments: {
        empty: 'No attachments selected.',
        remove: 'Remove',
        removeNamed: 'Remove {name}',
        summaryOne: '{count} file • {size}',
        summaryOther: '{count} files • {size}',
        more: '+{count} more',
        showLess: 'Show less',
        attachFiles: 'Attach files (.pdf .docx .xlsx .zip)',
        dropFiles: 'Drop files to attach',
        unsupportedFiles: '{files} not supported. Allowed: PDF, DOCX, XLSX, ZIP.',
      },
      imap: {
        title: 'Connect IMAP account',
        closeDialog: 'Close IMAP connect dialog',
        emailAddress: 'Email address',
        host: 'Host',
        port: 'Port',
        username: 'Username',
        password: 'Password',
        folder: 'Folder',
        useSsl: 'Use SSL',
        needHelp: 'Need help?',
        helpIntro: 'Find IMAP setup details in your mailbox provider docs (search “<provider> IMAP settings”).',
        helpHost: 'Host',
        helpHostBody: 'IMAP server hostname from provider docs',
        helpPort: 'Port',
        helpPortBody: '993 when SSL is enabled',
        helpUsername: 'Username',
        helpUsernameBody: 'usually your full email address',
        helpPassword: 'Password',
        helpPasswordBody: 'mailbox password or app password',
        helpFolder: 'Folder',
        helpFolderBody: 'INBOX (unless your provider says otherwise)',
        cancel: 'Cancel',
        connect: 'Connect',
        promptEmail: 'IMAP account email:',
        promptHost: 'IMAP host (e.g., imap.example.com):',
        promptPort: 'IMAP port:',
        promptUsername: 'IMAP username:',
        promptPassword: 'IMAP password (or app password):',
        promptFolder: 'IMAP folder:',
        promptUseSsl: 'Use SSL? yes/no',
        validationEmailRequired: 'Email address is required.',
        validationHostRequired: 'IMAP host is required.',
        validationPortRange: 'IMAP port must be between 1 and 65535.',
        validationUsernameRequired: 'IMAP username is required.',
        validationPasswordRequired: 'IMAP password is required.',
      },
      composeUi: {
        replyComposition: 'Reply composition',
        sendKind: 'Send kind',
        sendKindReply: 'Reply',
        sendKindNew: 'New',
        composerTitleReply: 'Reply',
        composerTitleNew: 'New email',
        sendKindReplyTooltip: 'Send as a response to the last email in this thread.',
        sendKindNewTooltip: 'Draft a new outbound email outside this thread.',
        to: 'To:',
        subject: 'Subject:',
        copyDraft: 'Copy draft',
        draftHistory: 'Draft history',
        draftBodyPlaceholder: 'Start typing or use Generate.',
        draftBodyPlaceholderManual: 'Start typing your reply here.',
        updatedAt: 'Updated at --:--',
        updatedAtValue: 'Updated at {time}',
        regenerate: 'Regenerate',
        polish: 'Polish',
        undo: 'Undo',
        generateDraft: 'Generate draft',
        generateQuoteDraft: 'Generate quote draft',
        quoteOutput: 'Quote output',
        quoteOutputLabel: 'Include in reply',
        quoteOutputEmail: 'Quote in email',
        quoteOutputDocument: 'PDF document',
        quoteDocumentSettings: 'Document settings',
        quoteOutputEmailTooltip: 'Insert the reviewed calculation directly into the email body.',
        quoteOutputDocumentTooltip: 'Attach a branded PDF quote document generated from the reviewed calculation. With both options on, the email keeps the full quote and the PDF is attached.',
        quoteDocumentReady: 'Quote PDF ready',
        quoteDocumentStale: 'Quote PDF needs updating',
        quoteDocumentReviewedMeta: 'from reviewed calculation',
        quoteDocumentStaleMeta: 'calculation changed',
        regenerateDocument: 'Update PDF',
        quoteDocumentGenerateFailed: 'Unable to generate quote PDF.',
        quoteDocumentRequired: 'Generate the quote PDF before sending.',
        calculationReviewRequired: 'Complete and review the calculation preview before generating a quote.',
        generateClarificationDraft: 'Generate clarification draft',
        generateWithNotes: 'Generate with notes',
        selectClarifyingQuestions: 'Select clarifying questions',
        sending: 'Sending...',
        openProvider: 'Open in Gmail/Outlook',
        processRfq: 'Process RFQ',
        processing: 'Processing...',
        copyEmailText: 'Copy email text',
        clarifyTooltip: 'Draft a follow-up email with selected clarification points.',
        quoteTooltip: 'Draft a direct quote response email.',
        notesPlaceholder: 'Write what you want to say and AI assistant will include it in the generated reply.',
        clarificationPoints: 'Clarification points',
        selectAll: 'Select all',
        deselectAll: 'Deselect all',
        selectedCount: '0 selected',
        selectedCountValue: '{count} selected',
        clarificationPlaceholder: 'Clarification points will appear here.',
        showMoreClarifications: 'Show more clarification points',
        showLessClarifications: 'Show fewer clarification points',
        clarifyHintChecking: 'Checking clarifications...',
        clarifyHintUnavailable: 'Clarification check unavailable',
        clarifyHintLikelyHigh: 'Clarifications likely (High)',
        clarifyHintPossibleMedium: 'Clarifications possible (Medium)',
        clarifyHintUnlikelyLow: 'Clarifications unlikely (Low)',
        clarifyHintNotNeeded: 'Clarifications not needed',
        clarifyHintLikely: 'Clarifications likely',
        diffHintGeneratedWithQuestions: 'Generated draft with Questions section.',
        diffHintGeneratedNewDraft: '',
        diffHintAddedQuestionsSection: 'Added Questions section.',
        diffHintPolishedDraft: 'Polished the draft.',
        diffHintUpdatedDraft: 'Updated the draft.',
        diffHintRestoredPreviousVersion: 'Restored previous version.',
        sendDisabledStartTyping: 'Start typing to enable Send.',
        sendDisabledAddRecipient: 'Add at least one valid recipient email in To.',
        generateDisabledClarify: 'Select clarifying questions or add notes to enable Generate.',
        generateDisabledQuote: 'Add notes or select clarifying questions to enable Generate.',
      },
      rfq: {
        replySubjectFallback: 'Re: Your RFQ',
        loadedFromCache: 'Loaded from cache',
        processed: 'Processed',
        processedBy: 'processed by {name}',
        processedAgo: 'Processed {timeAgo}',
        noAttachmentsToProcess: 'This email has no attachments to process.',
        processingFollowup: 'Processing follow-up…',
        followupUpdated: 'Follow-up updated',
        followupFailed: 'Follow-up processing failed. Review latest thread updates.',
        processingRfq: 'Processing RFQ…',
        done: 'Done',
        processingFailed: 'Processing failed',
        waitingForAutoprocess: 'Waiting for auto-processing…',
        reviewModeManual: 'Review mode is manual. Mark RFQ to continue.',
        selectMessageFirst: 'Select a message first.',
        messageIdMissing: 'Unable to determine message id.',
      },
      common: {
        retry: 'Retry',
        copyFailed: 'Copy failed',
        saved: 'Saved',
        clear: 'Clear',
        close: 'Close',
        edit: 'Edit',
        open: 'Open',
        download: 'Download',
        remove: 'Remove',
        link: 'Link',
        unhide: 'Unhide',
        adminOnly: 'Admin only',
        dismissNotification: 'Dismiss notification',
        pending: 'Pending',
        notAvailable: 'Not available',
        localeLabels: {
          en: 'English',
          cs: 'Czech',
        },
      },
      priority: {
        bucketNormal: 'Normal',
        bucketHigh: 'High',
        bucketUrgent: 'Urgent',
        unavailable: 'Unavailable',
        updatedJustNow: 'Updated just now',
        updatedMinutesAgo: 'Updated {count}m ago',
        updatedHourAgo: 'Updated 1h ago',
        updatedHoursAgo: 'Updated {count}h ago',
        scoreAria: 'Priority score: {score} ({label})',
        scoreAriaUnavailable: 'Priority score: — ({label})',
        statusUnavailable: 'Score unavailable',
        noteHigher: 'This RFQ is higher priority than others.',
        noteLower: 'Other RFQs may be higher priority.',
        noteSimilar: 'This RFQ is similar in priority to others.',
      },
      intent: {
        labelUrgent: 'Urgent',
        labelHighValue: 'High potential',
        labelRoutine: 'Standard',
        labelLowInfo: 'Needs context',
      },
      priorityReasons: {
        buyerVip: 'VIP buyer ({buyer})',
        buyerPreferred: 'Preferred buyer ({buyer})',
        orderLarge: 'Large order ({lines} lines, avg qty ≈ {avg_qty})',
        orderMedium: 'Medium order ({lines} lines, avg qty ≈ {avg_qty})',
        duePast: 'Past due date',
        dueVerySoon: 'Due very soon',
        dueWithinWeek: 'Due within a week',
        ageAging: 'Aging in inbox',
        ageWaiting: 'Has been waiting a while',
        intentClassified: 'RFQ profile: {label}',
        fitPremiumMaterials: 'Includes premium materials',
        clarityImportantMissing: 'Important data missing (qty/material/due date)',
        claritySomeMissing: 'Some RFQ details missing',
        boostFresh: 'Fresh RFQ boost',
        boostOverdue: 'Overdue urgency boost',
      },
      compare: {
        onlyDifferences: 'Only differences',
        prevDiff: 'Prev diff',
        nextDiff: 'Next diff',
        swap: 'Swap',
        loadMore: 'Load more',
      },
      progress: {
        kpiDialogTitle: 'Update status?',
        kpiDialogBody: 'Marking this RFQ as Quoted will affect KPI metrics (Quotes Sent, Win/Outcome rates) across selected time windows. This may not be reversible in reports.',
        unassigned: 'Unassigned',
        teammate: 'Teammate',
        assignee: 'Assignee',
        outcome: 'Outcome',
      },
    },
    cs: {
      shell: {
        pageTitle: 'Poptávky · RFQ asistent',
        title: 'RFQ Asistent',
        rfqInboxAria: 'Doručené poptávky',
        analyticsOpen: 'Otevřít analytiku',
        analyticsLabel: 'Analytika',
      },
      login: {
        pageTitle: 'Přihlášení · Sovai',
        title: 'Přihlaste se do vašeho workspace',
        emailLabel: 'Pracovní e-mail',
        emailPlaceholder: 'vy@firma.cz',
        submit: 'Poslat přihlašovací odkaz',
        help: 'Na email vám pošleme přihlašovací odkaz. Heslo není potřeba.',
        devLinkReady: 'Vývojový odkaz je připraven:',
        continue: 'Pokračovat do inboxu',
        languageToggleAria: 'Vyberte jazyk rozhraní',
        languageLabel: 'Jazyk',
        languageCzech: 'Čeština',
        languageEnglish: 'English',
        languageCzechShort: 'CS',
        languageEnglishShort: 'EN',
        privacyPolicy: 'Zásady ochrany soukromí',
        cookieSettings: 'Nastavení cookies',
        termsOfUse: 'Podmínky použití',
        enterEmail: 'Zadejte platnou e-mailovou adresu.',
        sending: 'Odesílá se přihlašovací odkaz…',
        checkEmail: 'Zkontrolujte e-mail s přihlašovacím odkazem.',
        loginFailed: 'Přihlášení se nezdařilo.',
        errorInviteNotFound: 'Pozvánka nebyla nalezena.',
        errorInviteExpired: 'Platnost pozvánky vypršela.',
        errorInviteAccepted: 'Pozvánka už byla přijata.',
        errorInviteEmailMismatch: 'E-mail pozvánky neodpovídá tomuto přihlášení.',
        errorWorkspaceRequired: 'Pracovní prostor je povinný.',
        errorUnauthorizedWorkspace: 'Uživatel nemá oprávnění pro tento pracovní prostor.',
      },
      inbox: {
        searchLabel: 'Hledat v poptávkách',
        searchPlaceholder: 'Hledat poptávky, díly, materiály',
        sortAria: 'Seřadit poptávky',
        sortReceivedDesc: 'Přijato (nejnovější)',
        sortReceivedAsc: 'Přijato (nejstarší)',
        sortPriority: 'Priorita',
        sortStatus: 'Stav',
        workspaceRfqs: 'Sdílené poptávky',
        summaryDefault: 'Vše · Přijaté',
        back: 'Poptávky',
        review: 'Ke kontrole',
        filterLabel: 'Filtrovat seznam',
        filterMultiple: 'Více filtrů',
        filterClear: 'Vymazat',
        refresh: 'Obnovit poptávky',
        demoParsedScroll: 'Automaticky posunout parsovaná data',
        demoAssistantScroll: 'Automaticky posunout AI panel',
        demoReplay: 'Přehrát demo',
        filterButton: 'Filtrovat poptávky',
        sortButton: 'Seřadit poptávky',
        statuses: {
          all: 'Vše',
          new: 'Nové',
          inProgress: 'V řešení',
          pending: 'Čeká na odpověď',
          quoted: 'Odesláno',
          won: 'Vyhráno',
          lost: 'Ztraceno',
          archived: 'Archivováno',
        },
        statusAllTitle: 'Vše kromě skrytých',
        hidden: 'Skryté',
        emptyTitle: 'Zatím žádné poptávky',
        emptyBody: 'Připojte e-mailový účet v Nastavení nebo synchronizujte schránku a začněte zpracovávat poptávky.',
        emptySync: 'Synchronizovat schránku',
        emptySettings: 'Otevřít nastavení',
        settingsOpen: 'Otevřít nastavení',
        lastActivityTitle: 'Poslední aktivita',
        dateToday: 'Dnes',
        dateYesterday: 'Včera',
        filterLabelActive: 'Filtrovaný seznam ({count})',
        noMessages: 'Žádné zprávy',
        loadedMessages: 'Načteno {count} zpráv',
        syncing: 'Synchronizují se poptávky…',
        noConnectedAccounts: 'Nejsou připojené žádné e-mailové účty',
        refreshed: 'Poptávky byly obnoveny',
        syncFailed: 'Synchronizace se nezdařila',
        threadCountAria: '{count} zpráv ve vlákně',
        threadCountTitle: '{count} dalších e-mailů v tomto vlákně',
        threadNewBadge: '{count} nových',
        threadNewBadgeOne: '{count} nová',
        threadNewBadgeFew: '{count} nové',
        threadNewBadgeOther: '{count} nových',
        updateHiddenFailed: 'Nepodařilo se změnit stav skrytí',
        autoprocess: {
          queued: 'Čeká na zpracování',
          queuedTitle: 'Čeká na automatické zpracování',
          running: 'Zpracovává se',
          runningTitle: 'Probíhá automatické zpracování',
          failed: 'K opakování',
          failedTitle: 'Automatické zpracování se nezdařilo',
        },
        loadError: 'Nepodařilo se načíst poptávky.',
        loadErrorToast: 'Nepodařilo se načíst poptávky',
        threadLoading: 'Načítá se vlákno…',
      },
      preview: {
        emptyTitle: 'Není vybrána žádná zpráva',
        emptyBody: 'Vyberte poptávku ze seznamu, zobrazte si e-mail, přílohy a detaily požadavku a potom připravte odpověď v editoru.',
        subjectPlaceholder: 'Vyberte zprávu',
        needsReview: 'Ke kontrole',
        buyerFallback: 'Zákazník',
        from: 'Od:',
        to: 'Komu:',
        recipientsMore: '+{count} dalších',
        quickReplyAria: 'Rychlá odpověď',
        attach: 'Připojit soubory',
        inputLabel: 'Text odpovědi',
        inputPlaceholder: 'Napište sem odpověď...',
        openEditor: 'Otevřít editor',
        polish: 'Vylepšit text',
        generateQuote: 'Vygenerovat nabídku',
        minimizeQuickReply: 'Sbalit rychlou odpověď',
        copied: 'Zkopírováno!',
        copyEmailText: 'Kopírovat text e-mailu',
        copyFailed: 'Kopírování se nezdařilo',
        newMessage: 'Nová zpráva',
        newMessages: 'Nové zprávy',
        attachmentFallback: 'Příloha',
        attachmentFallbackNamed: 'Příloha {index}',
        attachmentsSingle: 'PŘÍLOHA (1)',
        attachmentsMultiple: 'PŘÍLOHY ({count})',
        openAttachment: 'Otevřít přílohu',
        downloadAttachment: 'Stáhnout přílohu',
        openAttachmentNamed: 'Otevřít přílohu {filename}',
        unknownSize: 'Neznámá velikost',
        providerOpenMissing: 'Jakmile bude odkaz k dispozici, otevřete svého poskytovatele e-mailu z náhledu.',
        send: 'Odeslat',
        draftClarification: 'Vygenerovat dotaz',
        threadActions: 'Akce vlákna',
        moreActions: 'Další akce',
        linkToRfq: 'Přiřadit k poptávce',
        copyRfqId: 'Kopírovat RFQ ID',
        notRfq: 'Není poptávka',
        notRfqReason: 'Označeno jako nepoptávka',
        notRfqConfirmTitle: 'Odebrat email z aplikace?',
        notRfqConfirmBody: 'Tento email označíme jako nepoptávku a přestane se zobrazovat v aplikaci. Samotný email se nesmaže.',
        notRfqConfirmCancel: 'Zrušit',
        notRfqConfirmConfirm: 'Odebrat z aplikace',
        rfqIdUnavailable: 'ID RFQ není k dispozici',
        rfqIdCopied: 'ID RFQ bylo zkopírováno',
        rfqIdCopyFailed: 'ID RFQ se nepodařilo zkopírovat',
        hideFromInbox: 'Skrýt ze seznamu',
        reportSupport: 'Nahlásit problém nebo vylepšení',
        messageCountOne: '{count} zpráva',
        messageCountFew: '{count} zprávy',
        messageCountOther: '{count} zpráv',
        newCountOne: '{count} nová zpráva',
        newCountFew: '{count} nové zprávy',
        newCountOther: '{count} nových zpráv',
        lastActivity: 'Poslední aktivita: {date}',
        noMessageContent: 'Žádný obsah zprávy.',
        showMore: 'Zobrazit více',
        showLess: 'Zobrazit méně',
        attachmentSummaryOne: '{count} příloha ▾',
        attachmentSummaryFew: '{count} přílohy ▾',
        attachmentSummaryOther: '{count} příloh ▾',
        copyToClipboardSuccess: 'E-mail byl zkopírován do schránky.',
        copyToClipboardError: 'Kopírování se nezdařilo.',
      },
      composer: {
        title: 'Odpověď',
        controlsGroup: 'Ovládací prvky editoru',
        showOriginal: 'Zobrazit původní zprávu',
        hideOriginal: 'Skrýt původní zprávu',
        aiSettings: 'Nastavení AI',
        close: 'Zavřít editor',
        aiAssist: 'AI asistent',
        settingsSummary: 'Souhrn nastavení AI',
        language: 'Jazyk',
        languageSettings: 'Nastavení jazyka',
        tone: 'Tón',
        toneSettings: 'Nastavení tónu',
        length: 'Délka',
        lengthSettings: 'Nastavení délky',
        signature: 'Podpis',
        signatureSettings: 'Nastavení podpisu',
        toneLengthSettings: 'Nastavení tónu a délky',
        draftMode: 'Režim konceptu',
        modeClarify: 'Dotaz',
        modeQuote: 'Nabídka',
        draftPlaceholder: 'Napište svou zprávu...',
        lastNotes: 'Poslední poznámky',
        cancel: 'Zrušit',
        recommended: 'Doporučeno',
        noMessageContent: 'Obsah zprávy není k dispozici.',
        selectMessageBeforeSend: 'Před odesláním vyberte zprávu.',
        startTypingToSend: 'Začněte psát, aby bylo možné odeslat zprávu.',
        invalidRecipient: 'Do pole Komu přidejte alespoň jednu platnou e-mailovou adresu.',
        sendCanceled: 'Odeslání bylo zrušeno.',
        sendAccountMissing: 'Nepodařilo se určit účet pro odeslání.',
        reconnectMailAccount: 'Znovu připojte e-mailový účet v Nastavení > E-mailové účty a potom odeslání opakujte.',
        attachmentRequired: 'Před odesláním nahrajte alespoň jednu připravenou přílohu nabídky.',
        sendFailed: 'Odeslání se nezdařilo. Zkuste to prosím znovu.',
        copyFailed: 'Kopírování se nezdařilo',
        uploadRfqFirst: 'Nejprve nahrajte poptávku.',
        draftError: 'Nepodařilo se připravit návrh',
        polishNeedText: 'Nejprve přidejte text k vylepšení.',
        polishNeedMoreDetail: 'Před vylepšením doplňte více detailů (asi 15 slov).',
        historyAiDraft: 'AI návrh',
        historyRegenerated: 'Vygenerováno znovu',
        historyPolished: 'Text byl vylepšen',
        polishStatus400: 'Pro vylepšení doplňte o něco více textu.',
        polishStatus404: 'Poptávka nebyla nalezena',
        polishStatus408: 'Vylepšení textu vypršelo. Zkuste to znovu.',
        polishStatus422: 'Text nešlo vylepšit bez změny významu. Původní znění zůstalo beze změny.',
        polishStatus502: 'Vylepšení textu vypršelo. Zkuste to znovu.',
        polishGenericError: 'Vylepšení textu se nezdařilo. Zkuste to znovu.',
      },
      aiSettings: {
        title: 'Nastavení AI',
        helper: 'Použije se při dalším konceptu',
        language: 'Jazyk',
        automatic: 'Automaticky',
        manualLanguageChoices: 'Ruční volba jazyka',
        czech: 'Čeština',
        german: 'Němčina',
        english: 'Angličtina',
        toneLength: 'Tón a délka',
        tone: 'Tón',
        toneFormal: 'Formální',
        toneNeutral: 'Neutrální',
        toneFriendly: 'Vstřícný',
        length: 'Délka',
        lengthConcise: 'Stručná',
        lengthMedium: 'Střední',
        lengthDetailed: 'Podrobná',
        display: 'Zobrazení',
        showStatusChips: 'Zobrazit aktuální nastavení AI',
      },
      signature: {
        appendOptions: 'Možnosti',
        none: 'Bez podpisu',
        noneHelp: 'Nepřipojí se žádný podpis.',
        basicText: 'Textový podpis',
        basicTextHelp: 'Doporučeno — čistý víceřádkový podpis.',
        customHtml: 'Vlastní HTML',
        customHtmlHelp: 'Pokročilé — použijte schválené HTML společnosti.',
        details: 'Detaily podpisu',
        textHeading: 'Textový podpis',
        textHelper: 'Vyplňte tolik údajů, kolik chcete sdílet.',
        name: 'Jméno *',
        roleCompany: 'Pozice / společnost',
        phone: 'Telefon',
        email: 'E-mail',
        reset: 'Resetovat',
        htmlHeading: 'Vlastní HTML podpis',
        htmlHelper: 'Vložte schválené HTML. Před použitím ho vyčistíme.',
        htmlSource: 'Zdroj HTML',
        htmlSave: 'Vyčistit a uložit',
        preview: 'Náhled',
        toastHtmlMissing: 'Chybí vlastní HTML podpis. Přidejte ho v nastavení, pokud chcete použít firemní blok.',
        toastDemo: 'Používá se ukázkový podpis. Uložte své údaje pro personalizaci.',
        toastSaved: 'Podpis byl uložen.',
        toastCleared: 'Podpis byl vymazán.',
        toastSavedClearedHtml: 'Podpis byl uložen — vlastní HTML bylo odstraněno.',
        toastSanitizedEmpty: 'Podpis byl uložen — po vyčištění nezůstal v HTML žádný povolený obsah.',
      },
      research: {
        title: 'Profil zákazníka',
        close: 'Zavřít detail profilu zákazníka',
        snapshot: 'Přehled',
        industries: 'Odvětví',
        materials: 'Materiály',
        capabilities: 'Výrobní možnosti',
        certifications: 'Certifikace',
        companyDetails: 'Firemní údaje',
        riskFlags: 'Rizikové signály',
        sources: 'Zdroje',
        selectMessageFirst: 'Nejprve vyberte zprávu s údaji o společnosti.',
        selectMessageToAnalyze: 'Vyberte zprávu k analýze.',
        analyzeSender: 'Prověřit zákazníka',
        runToView: 'Spusťte prověření pro zobrazení detailů.',
        refresh: 'Obnovit',
        copyTalkingPoints: 'Kopírovat hlavní body',
        noSummaryYet: 'Souhrn zatím není k dispozici.',
        notAvailable: 'Není k dispozici',
        noResearchSummary: 'Souhrn průzkumu zatím není k dispozici.',
        copyTalkingPointsSuccess: 'Hlavní body byly zkopírovány',
        copyTalkingPointsError: 'Nepodařilo se zkopírovat do schránky',
        moreLabel: 'Dalších {count} × {label}',
        fallbackDefaultMessage: 'Nepodařilo se dohledat důvěryhodné informace.',
        fallbackBusyTimedOut: 'Služba je zaneprázdněná nebo vypršel časový limit.',
        missingCompanyDetails: 'Chybí údaje o společnosti.',
        requestTimedOut: 'Prověření vypršelo. Zkuste to znovu.',
        requestFailed: 'Prověření se nezdařilo.',
        requestFailedRetry: 'Prověření se nezdařilo. Zkuste to prosím znovu.',
        progressResearching: 'Prověřuje se: {company} …',
        companyFallback: 'společnost',
        resultCompanyFallback: 'Společnost',
        likelyBuyerType: 'Pravděpodobný typ zákazníka: {type}',
        badgeCached: 'Z cache',
        badgeUpdated: 'Aktualizováno',
        badgeLive: 'Aktuální',
        badgeFallback: 'Náhradní výsledek',
        latestFreshToast: 'Poslední prověření je mladší než 24 hodin.',
        refreshUsedToast: 'Obnovení už bylo pro tuto poptávku použito.',
        topCitedFacts: 'Nejdůležitější zjištění:',
        website: 'Web',
        headquarters: 'Sídlo',
        employees: 'Počet zaměstnanců',
        annualRevenue: 'Roční obrat',
        financialPeriod: 'Účetní období',
        shippingRegions: 'Dodávané regiony',
        sourceFallback: 'Zdroj',
        relativeJustNow: 'právě teď',
        relativeMinutesAgo: 'před {count} min',
        relativeHoursAgo: 'před {count} h',
        relativeDayAgo: 'před 1 dnem',
        relativeDaysAgo: 'před {count} dny',
      },
      related: {
        title: 'Související poptávky',
        summaryPlaceholder: 'Vyberte zprávu, chcete-li zobrazit související poptávky.',
        summaryUnavailable: 'Nejprve zpracujte tuto poptávku, aby bylo možné zobrazit související položky.',
        tabs: {
          duplicates: 'Duplicitní',
          company: 'Stejná firma',
          similar: 'Podobné',
          lookup: 'Vyhledat',
        },
        empty: {
          duplicates: 'K této zprávě nebyly nalezeny duplicitní poptávky.',
          company: 'Od této firmy zatím nejsou žádné další poptávky.',
          similar: 'Zvoleným filtrům neodpovídají žádné podobné poptávky.',
          lookup: 'Začněte psát a vyhledejte poptávky v tomto workspace.',
        },
        compare: 'Porovnat',
        openEmail: 'Otevřít e-mail',
        quoteNotFound: 'Nabídka nebyla nalezena',
        quoteLoading: 'Načítá se nabídka…',
        quoteRetry: 'Zkusit znovu načíst nabídku',
        quoteViewSent: 'Zobrazit odeslanou nabídku',
        noSavedTags: 'K této poptávce nejsou uložené žádné štítky.',
        loadMore: 'Načíst další',
        materials: 'Materiály (oddělené čárkou)',
        norms: 'Normy (oddělené čárkou)',
        partNumbers: 'Čísla dílů (oddělená čárkou)',
        retry: 'Zkusit znovu',
        currentRfq: 'Aktuální poptávka',
        comparedRfq: 'Porovnávaná poptávka',
        details: 'Detail',
        lookupPlaceholder: 'Hledat podle firmy, kontaktu nebo předmětu',
        lookupLoading: 'Vyhledávají se poptávky…',
        lookupHelper: 'Zadejte alespoň dva znaky. Výsledky jsou omezené na tento workspace.',
        lookupMinChars: 'Pro hledání zadejte alespoň dva znaky.',
        unavailableMessage: 'Nejprve zpracujte tuto poptávku, aby bylo možné zobrazit související položky.',
        messageUnavailable: 'Zpráva není k dispozici.',
        openMessageFailed: 'Zprávu se nepodařilo otevřít.',
        openSentQuoteFailed: 'Odeslanou nabídku se nepodařilo otevřít.',
        quoteLoadFailed: 'Odeslanou nabídku se nepodařilo načíst.',
        sameCompanyOnly: 'Omezit na stejnou firmu',
        findingSimilar: 'Vyhledávají se podobné poptávky…',
        similarLoadFailed: 'Nepodařilo se najít podobné poptávky.',
        loadingRelated: 'Načítají se související poptávky…',
        relatedLoadFailed: 'Nepodařilo se načíst související poptávky.',
        unknownCompany: 'Neznámá firma',
        chipMaterial: 'Materiál',
        chipNorm: 'Norma',
        chipPart: 'Díl',
      },
      notes: {
        deleteEntry: 'Smazat sdílenou poznámku',
        scrollLatest: 'Posunout na nejnovější poznámky',
        latest: 'Nejnovější',
        deleted: 'Poznámka byla smazána',
        undo: 'Vrátit zpět',
        concurrentUpdate: 'Sdílenou poznámku mezitím upravil někdo jiný. Pro pokračování obnovte stránku.',
        alreadyRemoved: 'Sdílená poznámka už byla odstraněna.',
        deleteOwnOnly: 'Mazat můžete pouze své vlastní položky.',
        restored: 'Poznámka byla obnovena',
        undoFailed: 'Nepodařilo se vrátit akci zpět',
        shareHint: 'Upravte a publikujte poznámku pro sdílení s týmem.',
        loadFailed: 'Nepodařilo se načíst poznámky',
        published: 'Sdílená poznámka byla publikována',
        publishFailed: 'Publikování se nezdařilo',
        publishing: 'Publikuje se…',
        appendOnly: 'Do sdílené poznámky můžete přidávat další text, ale ne mazat již zveřejněný obsah.',
        noteTooLong: 'Sdílené poznámky jsou omezené na {count} znaků na jednu položku.',
        someone: 'Někdo',
        today: 'Dnes',
        yesterday: 'Včera',
        undated: 'Bez data',
        sharedBy: 'Autor: {author}',
        relativeJustNow: 'právě teď',
        relativeMinuteAgo: 'před 1 minutou',
        relativeMinutesAgo: 'před {count} minutami',
        relativeHourAgo: 'před 1 hodinou',
        relativeHoursAgo: 'před {count} hodinami',
        relativeDayAgo: 'před 1 dnem',
        relativeDaysAgo: 'před {count} dny',
      },
      settings: {
        title: 'Nastavení',
        close: 'Zavřít nastavení',
        profile: 'Profil',
        preferences: 'Předvolby',
        appearance: 'Vzhled',
        signedInAs: 'Přihlášen jako',
        signOut: 'Odhlásit se',
        mailAccounts: 'E-mailové účty',
        defaultOutbound: 'Výchozí účet pro odesílání',
        defaultNone: 'Bez výchozího účtu (vždy vyžadovat výběr)',
        save: 'Uložit',
        noAccounts: 'Zatím nejsou připojené žádné účty.',
        gmail: 'Gmail',
        outlook: 'Outlook',
        imap: 'IMAP',
        connect: 'Připojit',
        sync: 'Synchronizovat',
        disconnect: 'Odpojit',
        you: 'Vy',
        remove: 'Odebrat',
        pending: 'Čeká na potvrzení',
        users: 'Uživatelé',
        invites: 'Pozvánky',
        invite: 'Pozvat',
        language: 'Jazyk aplikace',
        languageEnglish: 'Angličtina',
        languageCzech: 'Čeština',
        interfaceSize: 'Velikost rozhraní',
        compact: 'Kompaktní',
        defaultSize: 'Výchozí',
        large: 'Velká',
        classic: 'Klasický',
        warm: 'Teplý',
        calculationDefaults: 'Výchozí hodnoty kalkulace',
      },
      assistant: {
        collapseAssistant: 'Sbalit přehled poptávky',
        expandAssistant: 'Rozbalit přehled poptávky',
        ariaLabel: 'Chytrý přehled',
        title: 'Přehled poptávky',
        paneActions: 'Akce panelu Chytrého přehledu',
        expandPane: 'Rozbalit panel Chytrého přehledu',
        collapsePane: 'Sbalit panel Chytrého přehledu',
        expandTitle: 'Rozbalit',
        collapseTitle: 'Sbalit',
        closePane: 'Zavřít panel Chytrého přehledu',
        closeTitle: 'Zavřít',
        readyTitle: 'Chytrý přehled je připraven',
        readyBody: 'Vyberte zprávu a zobrazte si shrnutí, extrahovaná data, rizikové signály a další informace.',
        capabilitiesAria: 'Možnosti Chytrého přehledu',
        cardSummary: 'Shrnutí',
        cardBuyerIntent: 'Profil poptávky',
        cardRiskFlags: 'Rizikové signály',
        cardParsedData: 'Extrahovaná data',
      },
      rfqOverview: {
        selectMessage: 'Vyberte zprávu',
        from: 'Od:',
        due: 'Termín',
        unassigned: 'Nepřiřazeno',
        assignToMe: 'Přiřadit sobě',
        assignedToMe: 'Přiřazeno mně',
        loadingTeammates: 'Načítám řešitele...',
        buyerIntent: 'Profil poptávky',
        priorityScore: 'Priorita ve frontě',
        intentConfidence: 'Jistota: {score} %.',
        cellEditedByAt: 'Upravil(a) {name} · {time}',
        cellEditedBy: 'Upravil(a) {name}',
        cellEditedAt: 'Upraveno · {time}',
        cellEditedManual: 'Ručně upraveno',
        priorityRankOf: 'z {total}',
        priorityBusinessScore: 'Skóre priority: {score}/100',
        quoteReady: 'Připraveno k nabídce',
        clarificationNeeded: 'Chybějící údaje',
        clarificationOptional: 'Upřesnění volitelné',
        clarificationSuggested: 'Upřesnění doporučeno',
        openQuoteDraft: 'Vygenerovat nabídku',
        openClarifyingDraft: 'Vygenerovat dotaz',
        dueTodayTitle: 'Termín je dnes',
        dueInDaysTitle: 'Termín za {count} d',
        overdueByDaysTitle: 'Po termínu o {count} d',
        showRiskRationale: 'Zobrazit důvod rizika',
        why: 'Proč?',
        reasonMaterial: 'Chybí specifikace materiálu',
        reasonQuantity: 'Chybí požadované množství',
        reasonDeadline: 'Chybí termín',
        reasonSpec: 'Chybí tolerance nebo povrchová úprava',
        reasonSpecWithFields: 'Chybí {fields}',
        reasonNorm: 'Chybí příslušná norma',
        reasonFallback: 'Chybí informace pro doplnění',
        fieldDescription: 'název dílu',
        fieldPartNo: 'číslo součásti',
        fieldTolerance: 'tolerance',
        fieldHeatTreatment: 'tepelné zpracování',
        fieldSurfaceFinish: 'povrchová úprava',
        fieldConjunction: ' a ',
        reasonWithPart: '{reason} ({part})',
        reasonWithCount: '{reason} ({count} položek)',
        summary: 'Shrnutí',
        summaryLanguageBadgeAria: 'Jazyk shrnutí se liší od rozhraní',
        clarifyingLanguageBadgeAria: 'Jazyk doplňujících dotazů se liší od rozhraní',
        intentLanguageBadgeAria: 'Jazyk záměru kupujícího se liší od rozhraní',
        insightLanguageTooltipSummary: 'Shrnutí je uloženo v {sourceLanguage}. Kliknutím jej vygenerujte znovu v {targetLanguage}.',
        insightLanguageTooltipClarifying: 'Doplňující dotazy jsou uložené v jazyce {sourceLanguage}. Kliknutím je vygenerujete znovu v jazyce {targetLanguage}.',
        insightLanguageTooltipIntent: 'Záměr kupujícího je uložený v jazyce {sourceLanguage}. Kliknutím jej vygenerujete znovu v jazyce {targetLanguage}.',
        insightLanguageLoading: '{language}...',
        languageNameEn: 'angličtině',
        languageNameCs: 'češtině',
        summaryRefreshFailed: 'Shrnutí se nepodařilo vygenerovat znovu.',
        clarifyingRefreshFailed: 'Doplňující dotazy se nepodařilo vygenerovat znovu.',
        intentRefreshFailed: 'Záměr kupujícího se nepodařilo vygenerovat znovu.',
        loadingSummary: 'Načítám shrnutí...',
        summaryUnavailable: 'Shrnutí není k dispozici.',
        noSummaryAvailable: 'Shrnutí není k dispozici.',
        selectMessageSummary: 'Vyberte zprávu pro zobrazení shrnutí.',
        idle: 'čeká',
        showMore: 'Zobrazit více',
        related: 'Související',
        viewAllRelatedRfqs: 'Zobrazit všechny související poptávky',
        relatedMessage: 'Související poptávka',
        unknownSender: 'Neznámý odesílatel',
        relationDuplicate: 'Duplicitní',
        relationSameSender: 'Stejný odesílatel',
        relationSimilar: 'Podobná',
        relationRelated: 'Související',
        relatedMessageUnavailable: 'Související zprávu se nepodařilo otevřít. Podrobnosti najdete níže v souvisejících poptávkách.',
        relatedMessageNotLoaded: 'Související zpráva nebyla v doručených načtena. Podrobnosti najdete níže v souvisejících poptávkách.',
        lineItemsOne: '{count} položka',
        lineItemsFew: '{count} položky',
        lineItemsOther: '{count} položek',
        materialsOne: '{count} materiál',
        materialsFew: '{count} materiály',
        materialsOther: '{count} materiálů',
        parsedData: 'Extrahovaná data',
        showParsedMappingDetails: 'Zobrazit detail mapování',
        export: 'Export',
        exportXlsx: 'Export XLSX',
        exportCsv: 'Export CSV',
        exportMkset: 'Exportovat pro MK SET',
        profileFields: 'Pole pro klienta',
        allDetected: 'Všechna nalezená data',
        viewToggleProfile: 'MK SET',
        viewToggleAll: 'Vše',
        exportGroupMkset: 'MK SET export',
        exportGroupMksetAria: 'O MK SET exportu',
        exportGroupMksetInfo: 'Tři poziční sloupce pro import do systému MK SET (množství, popis, číslo dílu). Bez hlavičky.',
        exportGroupComplete: 'Kompletní export',
        exportGroupCompleteAria: 'O kompletním exportu',
        exportGroupCompleteInfo: 'Všechny sloupce zobrazené v aktuální tabulce extrahovaných dat (pole pro MK SET nebo všechna nalezená data).',
        exportFull: 'Kompletní export',
        scrollMoreColumns: 'Pro další sloupce posuňte tabulku vodorovně',
        viewAllRows: 'Zobrazit všechny řádky',
        viewAllRowsCount: 'Zobrazit všech {count} řádků',
        mappedSummary: '{mapped}/{total} namapováno',
        readMore: 'Číst dál',
        readLess: 'Skrýt',
      },
      review: {
        markRfq: 'Označit jako poptávku',
        markRfqTooltip: 'Zařadí zprávu do inboxu poptávek a případně ji automaticky propojí s existující poptávkou.',
        linkRfq: 'Přiřadit k poptávce',
        linkRfqTooltip: 'Ručně přiřadí zprávu k existující poptávce.',
        ignore: 'Tohle není poptávka',
        ignoreTooltip: 'Odebere zprávu z RFQ asistenta (ve vašem inboxu zůstane).',
        linkExistingRfq: 'Přiřadit k existující poptávce',
        close: 'Zavřít',
        searchPlaceholder: 'Hledat podle firmy, kontaktu nebo předmětu',
        searchEmpty: 'Pro hledání zadejte alespoň 2 znaky.',
        quickActions: 'Rychlé akce',
        expandQuickReply: 'Rozbalit rychlou odpověď',
        selectRfqToLink: 'Vyberte poptávku k přiřazení.',
        linkFailed: 'Nepodařilo se přiřadit zprávu',
        classificationFailed: 'Nepodařilo se změnit zařazení zprávy',
      },
      notesUi: {
        title: 'Poznámky',
        scope: 'Rozsah poznámek',
        private: 'Soukromé',
        workspace: 'Sdílené',
        privateNote: 'Soukromá poznámka',
        privatePlaceholder: 'Pište poznámky pro sebe. Zůstanou viditelné jen vám.',
        workspaceNote: 'Sdílená poznámka',
        workspacePlaceholder: 'Sdílejte poznámku s celým týmem.',
        refreshNotes: 'Obnovit poznámky',
        publish: 'Publikovat',
      },
      draftSettings: {
        close: 'Zavřít',
        save: 'Uložit',
      },
      analytics: {
        title: 'Analytika',
        close: 'Zavřít analytiku',
        moreComing: 'Více již brzy',
      },
      sendAccountPicker: {
        eyebrow: 'Nové odeslání',
        title: 'Vyberte účet odesílatele',
        close: 'Zavřít výběr účtu',
        subtitle: 'Vyberte účet, ze kterého chcete tuto zprávu odeslat.',
        options: 'Možnosti účtu pro odeslání',
        cancel: 'Zrušit',
        send: 'Odeslat',
      },
      settingsUi: {
        languageHelp: 'Vyberte jazyk, který se bude používat v celé aplikaci.',
        languageSelectAria: 'Jazyk rozhraní',
        displayNameLabel: 'Jméno',
        displayNamePlaceholder: 'Jak vás mají vidět kolegové',
        saveDisplayName: 'Uložit jméno',
        savingDisplayName: 'Ukládá se jméno…',
        displayNameSaved: 'Jméno bylo uloženo.',
        displayNameSaveFailed: 'Jméno se nepodařilo uložit.',
        themeHelp: 'Vyberte mezi klasickým bílým pracovním prostorem a teplejším podkladem.',
        interfaceSizeHelp: 'Upravte celkové měřítko rozhraní pro lepší čitelnost.',
        interfaceSizeSelectAria: 'Velikost rozhraní',
        applyingLanguage: 'Používá se zvolený jazyk…',
        unknownAccount: 'neznámý účet',
        lastSync: 'Poslední synchronizace {date}',
        confirmDisconnect: 'Odpojit účet {email}?',
        disconnecting: 'Účet se odpojuje…',
        accountDisconnected: 'Účet byl odpojen.',
        disconnectFailed: 'Účet se nepodařilo odpojit.',
        confirmRemoveMember: 'Odebrat uživatele {userId} z tohoto týmu?',
        removingUser: 'Uživatel se odebírá…',
        userRemoved: 'Uživatel byl odebrán.',
        removeUserFailed: 'Uživatele se nepodařilo odebrat.',
        expiresAt: 'Platnost do {date}',
        adminLabel: 'Administrátor',
        memberLabel: 'Člen',
        openConsent: 'Otevírá se souhlas pro {provider}…',
        oauthStartFailed: 'Nepodařilo se spustit OAuth pro {provider}.',
        connectingImap: 'Připojuje se účet IMAP…',
        imapConnected: 'Účet IMAP byl připojen.',
        imapConnectFailed: 'Účet IMAP se nepodařilo připojit.',
        noProviderAccounts: 'Pro {provider} nejsou k dispozici žádné účty k synchronizaci.',
        syncingProvider: 'Synchronizuje se {provider}…',
        syncedProviderSummary: 'Synchronizace {provider}: {inserted} nových · {updated} aktualizovaných',
        syncProviderFailed: 'Nepodařilo se synchronizovat {provider}.',
        savingDefaultOutbound: 'Ukládá se výchozí účet pro odesílání…',
        defaultOutboundSaved: 'Výchozí účet pro odesílání byl uložen.',
        defaultOutboundSaveFailed: 'Výchozí účet pro odesílání se nepodařilo uložit.',
        enterInviteEmail: 'Zadejte e-mail pro pozvánku.',
        sendingInvite: 'Pozvánka se odesílá…',
        inviteCreated: 'Pozvánka byla vytvořena.',
        inviteCreatedWithLink: 'Pozvánka byla vytvořena. Odkaz: {link}',
        inviteFailed: 'Pozvánku se nepodařilo vytvořit.',
        resendInvite: 'Odeslat znovu',
        confirmResendInvite: 'Odeslat pozvánku znovu na {email}?',
        resendingInvite: 'Pozvánka se znovu odesílá…',
        inviteResent: 'Pozvánka byla znovu odeslána.',
        resendInviteFailed: 'Pozvánku se nepodařilo znovu odeslat.',
        loadMembersFailed: 'Nepodařilo se načíst členy týmu.',
        loadInvitesFailed: 'Nepodařilo se načíst pozvánky.',
        loadMailAccountsFailed: 'Nepodařilo se načíst e-mailové účty.',
        mailSectionGmailAria: 'Účty Gmail',
        mailSectionOutlookAria: 'Účty Outlook',
        mailSectionImapAria: 'Účty IMAP',
        syncGmail: 'Synchronizovat Gmail',
        syncOutlook: 'Synchronizovat Outlook',
        syncImap: 'Synchronizovat IMAP',
        noGmailAccounts: 'Žádné účty Gmail nejsou připojené.',
        noOutlookAccounts: 'Žádné účty Outlook nejsou připojené.',
        noImapAccounts: 'Žádné účty IMAP nejsou připojené.',
        usersHint: 'Administrátoři mohou zvát kolegy a spravovat přístupy.',
        inviteEmailPlaceholder: 'jmeno@example.com',
        inviteRole: 'Role pozvánky',
        roleMember: 'Člen',
        roleAdmin: 'Administrátor',
        sendInvite: 'Odeslat pozvánku',
        pendingInvites: 'Čekající pozvánky',
        noPendingInvites: 'Žádné čekající pozvánky.',
        workspaceMembers: 'Členové týmu',
        noMembersFound: 'Nebyli nalezeni žádní členové.',
        calculationSettingsTitle: 'Nastavení kalkulace',
        calculationSettingsSubtitle: 'Výchozí hodnoty workspace pro nové kalkulační náhledy.',
        closeCalculationSettings: 'Zavřít nastavení kalkulace',
        calculationDefaultsHelp: 'Použijí se při vytvoření nového kalkulačního náhledu. Existující kalkulace si ponechají uložené hodnoty.',
        defaultHourlyRate: 'Výchozí hodinová sazba',
        defaultMarkup: 'Výchozí přirážka (%)',
        defaultSetupCost: 'Výchozí náklady na přípravu',
        currency: 'Měna',
        notSet: 'Nenastaveno',
        calculationReadOnly: 'Výchozí hodnoty kalkulace mohou měnit pouze administrátoři workspace.',
        saveCalculationDefaults: 'Uložit výchozí hodnoty',
        calculationDisclaimer: 'Kalkulační náhledy jsou orientační a vyžadují kontrolu člověkem.',
        calculationLoadFailed: 'Výchozí hodnoty kalkulace se nepodařilo načíst.',
        calculationInvalidNumber: 'Pole {label} musí obsahovat platné nezáporné číslo.',
        calculationInvalidCurrency: 'Měna musí být třípísmenný kód.',
        calculationValidationFailed: 'Zkontrolujte výchozí hodnoty kalkulace.',
        savingCalculationDefaults: 'Výchozí hodnoty kalkulace se ukládají…',
        calculationDefaultsSaved: 'Výchozí hodnoty kalkulace byly uloženy.',
        calculationSaveFailed: 'Výchozí hodnoty kalkulace se nepodařilo uložit.',
        materialDefaultsTitle: 'Výchozí materiály',
        materialDefaultsHelp: 'Použije se pro předvyplnění materiálových nákladů v nových kalkulačních náhledech. Jde o výchozí hodnoty workspace, ne aktuální tržní ceny.',
        materialDefaultsMaterialColumn: 'Materiál',
        materialDefaultsCostColumn: 'Náklad / kus',
        materialDefaultsCurrencyColumn: 'Měna',
        materialDefaultsNoteColumn: 'Poznámka',
        materialDefaultsActionsColumn: 'Akce',
        materialDefaultsReadOnly: 'Výchozí materiály mohou měnit pouze administrátoři workspace.',
        materialDefaultsAddTitle: 'Přidat výchozí materiál',
        materialDefaultsEditTitle: 'Upravit výchozí materiál',
        materialDefaultsMaterialField: 'Materiál',
        materialDefaultsCostField: 'Náklad / kus',
        materialDefaultsCurrencyField: 'Měna',
        materialDefaultsNoteField: 'Poznámka',
        materialDefaultsNamePlaceholder: 'EN AW-6082',
        materialDefaultsCostPlaceholder: '180,50',
        materialDefaultsNotePlaceholder: 'Volitelná poznámka pro odhad nebo nákup',
        materialDefaultsEmpty: 'Zatím nejsou nastavené žádné aktivní výchozí materiály.',
        materialDefaultsSave: 'Uložit výchozí materiál',
        materialDefaultsSaveChanges: 'Uložit změny',
        materialDefaultsDeactivate: 'Deaktivovat',
        materialDefaultsLoadFailed: 'Výchozí materiály se nepodařilo načíst.',
        materialDefaultsNameRequired: 'Název materiálu je povinný.',
        materialDefaultsInvalidCurrency: 'Měna musí být třípísmenný kód.',
        materialDefaultsValidationFailed: 'Zkontrolujte pole výchozího materiálu.',
        materialDefaultsSaving: 'Výchozí materiál se vytváří…',
        materialDefaultsSavingChanges: 'Výchozí materiál se ukládá…',
        materialDefaultsSaved: 'Výchozí materiál byl uložen.',
        materialDefaultsUpdated: 'Výchozí materiál byl upraven.',
        materialDefaultsSaveFailed: 'Výchozí materiál se nepodařilo uložit.',
        materialDefaultsConfirmDeactivate: 'Deaktivovat materiál {material}?',
        materialDefaultsDeactivating: 'Výchozí materiál se deaktivuje…',
        materialDefaultsDeactivated: 'Výchozí materiál byl deaktivován.',
        materialDefaultsDeleteFailed: 'Výchozí materiál se nepodařilo deaktivovat.',
        quoteBrandingTitle: 'Branding nabídkového dokumentu',
        quoteBrandingHelp: 'Použije se při generování brandované PDF nabídky.',
        quoteBrandCompanyName: 'Název firmy v dokumentu',
        quoteBrandCompanyNamePlaceholder: 'Howda Engineering',
        quoteBrandValidityDays: 'Výchozí platnost nabídky',
        quoteBrandLogo: 'Logo',
        quoteBrandLogoChoose: 'Vybrat logo',
        quoteBrandLogoNoFile: 'Není vybraný žádný soubor',
        quoteBrandLogoDropHint: 'nebo sem přetáhněte PNG/JPG',
        quoteBrandLogoCurrent: 'Aktuální: {filename}',
        quoteBrandLogoMissing: 'Logo zatím není nahrané.',
        quoteBrandLogoRemove: 'Odebrat logo',
        quoteBrandLogoRemoveFailed: 'Logo se nepodařilo odebrat.',
        quoteBrandPaymentTerms: 'Výchozí platební podmínky',
        quoteBrandPaymentTermsPlaceholder: 'Splatnost 14 dní',
        quoteBrandDeliveryTerms: 'Výchozí dodací podmínky',
        quoteBrandDeliveryTermsPlaceholder: 'Dodání dle potvrzeného termínu',
        quoteBrandFooterNote: 'Patička / právní poznámka',
        quoteBrandFooterNotePlaceholder: 'Ceny podléhají finálnímu potvrzení.',
        quoteBrandCompanyAddress: 'Adresa firmy',
        quoteBrandCompanyAddressPlaceholder: 'Průmyslová 12, 619 00 Brno',
        quoteBrandCompanyEmail: 'E-mail firmy',
        quoteBrandCompanyEmailPlaceholder: 'nabidky@firma.cz',
        quoteBrandCompanyPhone: 'Telefon firmy',
        quoteBrandCompanyPhonePlaceholder: '+420 123 456 789',
        quoteBrandCompanyRegId: 'IČO / DIČ',
        quoteBrandCompanyRegIdPlaceholder: 'IČO 12345678 · DIČ CZ12345678',
        quoteBrandShowBreakdown: 'Zobrazit v PDF rozpad ceny',
        quoteBrandShowMarkup: 'Zobrazit marži jako samostatný řádek rozpadu',
        quoteBrandIncomplete: 'Název firmy není vyplněný — vygenerované PDF použije náhradní název workspace.',
        quoteBrandingSaveHelp: 'Logo a texty dokumentu se uloží pro budoucí PDF nabídky.',
        saveQuoteBranding: 'Uložit branding',
      },
      calculationPreview: {
        open: 'Kalkulační náhled',
        title: 'Kalkulační náhled',
        indicative: 'Orientační',
        helper: 'Orientační kalkulace podle vytěžených údajů a nastavených sazeb. Před přípravou nabídky zkontrolujte všechny vstupy.',
        expand: 'Rozšířit kalkulační náhled',
        expandTitle: 'Rozšířit',
        collapse: 'Zmenšit kalkulační náhled',
        collapseTitle: 'Zmenšit',
        close: 'Zavřít kalkulační náhled',
        loading: 'Načítá se kalkulace…',
        summary: 'Souhrn kalkulace',
        currency: 'Měna',
        currencyNoConversion: 'Změna měny pouze upraví označení měny; zadané částky se automaticky nepřepočítají.',
        indicativeTotal: 'Orientační celkem',
        reviewDisclaimer: 'Orientační kalkulace — vyžaduje kontrolu člověkem.',
        reload: 'Načíst znovu',
        markReviewed: 'Označit jako zkontrolované',
        suggestStock: 'Navrhnout polotovar',
        stockSuggestionTitle: 'Návrh polotovaru',
        refreshStockSuggestion: 'Přepočítat návrh',
        stockSuggestionLoading: 'Kontroluje se CAD geometrie…',
        recommendedStockOption: 'Doporučená varianta',
        recommendedStockOptionShort: 'Doporučeno',
        selectedStockOption: 'Vybraná varianta',
        stockOption: 'Varianta',
        otherStockOptions: 'Další varianty',
        otherStockOptionsCount: 'Další varianty: {count}',
        applyStockOption: 'Vybrat',
        tightStockOption: 'Těsný polotovar',
        roundedStockOption: 'Zaokrouhlený rozměr',
        conservativeReserveOption: 'Konzervativní rezerva',
        stockPreviewTitle: 'Náhled obálky',
        stockPreviewHelper: 'Náhled obálky, ne přesný 3D model',
        stockPreviewUnavailable: 'Náhled není dostupný',
        envelopeVolume: 'Objem obálky',
        finishedVolumeShort: 'Díl',
        stockVolumeShort: 'Polotovar',
        stockGeometry: 'Geometrie polotovaru',
        stockDelta: 'Rozdíl',
        finishedVolume: 'Objem dílu',
        stockVolume: 'Objem polotovaru',
        addedEnvelopeVolume: 'Přidaná obálka',
        stockBasis: 'Podklad',
        stockBasisTooltip: 'Zobrazit podklad návrhu polotovaru',
        stockRule: 'Pravidlo',
        stockConfidence: 'Důvěra',
        stockRuleMillingBlockDefault: 'Výchozí přídavek pro frézování',
        stockRuleMillingBlockConservative: 'Konzervativní rezerva pro frézování',
        stockRuleTurningBarDefault: 'Výchozí přídavek pro soustružení',
        stockRuleSheetProfileDefault: 'Výchozí zacházení s plechem/profilem',
        stockConfidenceHigh: 'Vysoká důvěra',
        stockConfidenceMedium: 'Střední důvěra',
        stockConfidenceLow: 'Nízká důvěra',
        stockConfidenceReview: 'Ke kontrole',
        stockProcessMilling: 'Frézování',
        stockProcessTurning: 'Soustružení',
        stockProcessSheetProfile: 'Plech/profil',
        stockProcessUnknown: 'Neznámý',
        finishedEnvelope: 'Obálka dílu',
        suggestedStock: 'Polotovar',
        stockAllowance: 'Přídavek',
        perSide: 'strana',
        processType: 'Proces',
        stockSources: 'Zdroj',
        applyStockSuggestion: 'Vybrat doporučení',
        stockSelectedAction: 'Vybráno',
        stockApplied: 'Vybráno pro kalkulaci',
        stockSuggesting: 'Navrhuje se polotovar…',
        stockSuggested: 'Návrhy polotovaru jsou připravené ke kontrole.',
        stockSuggestFailed: 'Polotovar se nepodařilo navrhnout.',
        stockWarningMissingCad: 'Pro tuto položku nebyla nalezena CAD příloha.',
        stockWarningGeometryUnavailable: 'CAD geometrie nebyla dostupná.',
        stockWarningAssembly: 'CAD vypadá jako sestava nebo vícedílný soubor.',
        stockWarningUnits: 'Jednotky CAD nejsou známé.',
        stockWarningSheet: 'Plech/profil vyžaduje ruční kontrolu polotovaru.',
        stockWarningGeneric: 'Zkontrolujte tento návrh polotovaru.',
        sourceExtracted: 'Vytěženo',
        sourceDefault: 'Výchozí workspace',
        sourceMaterialDefault: 'Výchozí materiál',
        sourceManual: 'Ručně',
        sourceSystem: 'Výchozí systém',
        unsaved: 'Neuložené změny',
        reset: 'Obnovit',
        material: 'Materiál',
        operations: 'Operace',
        external: 'Externí zpracování',
        setup: 'Příprava',
        other: 'Ostatní',
        markup: 'Přirážka',
        operationName: 'Operace',
        minutesPerUnit: 'Minuty / kus',
        hourlyRate: 'Hodinová sazba',
        removeOperation: 'Odebrat operaci',
        externalName: 'Externí proces',
        costPerUnit: 'Náklad / kus',
        removeExternal: 'Odebrat externí proces',
        item: 'Položka',
        quantityShort: 'Množství',
        complete: 'Kompletní',
        incomplete: 'Neúplné',
        quantity: 'Množství',
        heatTreatment: 'Tepelné zpracování',
        surfaceFinish: 'Povrchová úprava',
        process: 'Proces',
        materialCostUnit: 'Materiálový náklad / kus',
        setupCost: 'Náklady na přípravu / dávku',
        otherCost: 'Ostatní náklady / dávku',
        markupPercent: 'Přirážka (%)',
        internalOperations: 'Interní operace',
        addOperation: 'Přidat operaci',
        noInternalOperations: 'Neuplatňují se žádné náklady na interní operace',
        externalProcessing: 'Externí zpracování',
        addExternal: 'Přidat externí proces',
        reviewed: 'Zkontrolováno',
        applyLatest: 'Použít nejnovější vytěžená data',
        counts: '{total} položek · {complete} kompletních · {incomplete} neúplných',
        excludesIncomplete: 'Celková částka nezahrnuje {count} neúplných položek.',
        allIncluded: 'Celková částka zahrnuje všechny položky kalkulace.',
        sourceStale: 'Vytěžená data poptávky se změnila. Než budete pokračovat, použijte nejnovější data.',
        conflict: 'Tuto kalkulaci mezitím upravil někdo jiný. Načtěte ji znovu.',
        saving: 'Ukládá se…',
        saved: 'Uloženo',
        saveConflict: 'Konflikt při ukládání',
        saveFailed: 'Kalkulaci se nepodařilo uložit.',
        loadFailed: 'Kalkulační náhled se nepodařilo načíst.',
        reviewing: 'Označuje se jako zkontrolované…',
        reviewedByHuman: 'Zkontrolováno člověkem',
        reviewFailed: 'Kalkulaci se nepodařilo označit jako zkontrolovanou.',
        issueQuantity: 'Množství musí být větší než nula.',
        issueOperations: 'Přidejte interní operaci nebo označte, že se neuplatňuje.',
        issueOperationName: 'Název operace je povinný.',
        issueExternalName: 'Název externího procesu je povinný.',
        issueMaterialMissing: 'V poptávce chybí specifikace materiálu.',
        issueHeatMissing: 'Nebyl vytěžen požadavek na tepelné zpracování.',
        issueFinishMissing: 'Nebyl vytěžen požadavek na povrchovou úpravu.',
        issueQuantityChanged: 'Množství v kalkulaci se liší od vytěženého množství.',
        issueZeroMaterial: 'Materiálový náklad je výslovně nastaven na nulu.',
        issueRequired: 'Chybí povinný vstup kalkulace.',
        issueNonNegative: 'Zadejte platné nezáporné číslo.',
        issueGeneric: 'Zkontrolujte tento vstup.',
        quoteRequiresReview: 'Před vygenerováním nabídky dokončete a zkontrolujte kalkulační náhled.',
      },
      composerAttachments: {
        empty: 'Nejsou vybrané žádné přílohy.',
        remove: 'Odebrat',
        removeNamed: 'Odebrat {name}',
        summaryOne: '{count} soubor • {size}',
        summaryFew: '{count} soubory • {size}',
        summaryOther: '{count} souborů • {size}',
        more: '+{count} dalších',
        showLess: 'Zobrazit méně',
        attachFiles: 'Připojit soubory (.pdf .docx .xlsx .zip)',
        dropFiles: 'Přetáhněte soubory pro připojení',
        unsupportedFiles: '{files} nejsou podporovány. Povolené formáty: PDF, DOCX, XLSX, ZIP.',
      },
      imap: {
        title: 'Připojit účet IMAP',
        closeDialog: 'Zavřít dialog připojení IMAP',
        emailAddress: 'E-mailová adresa',
        host: 'Hostitel',
        port: 'Port',
        username: 'Uživatelské jméno',
        password: 'Heslo',
        folder: 'Složka',
        useSsl: 'Použít SSL',
        needHelp: 'Potřebujete poradit?',
        helpIntro: 'Podrobnosti k nastavení IMAP najdete v dokumentaci svého poskytovatele e-mailu (hledejte „<provider> IMAP settings“).',
        helpHost: 'Hostitel',
        helpHostBody: 'adresa serveru IMAP podle dokumentace poskytovatele',
        helpPort: 'Port',
        helpPortBody: '993, pokud je zapnuté SSL',
        helpUsername: 'Uživatelské jméno',
        helpUsernameBody: 'obvykle vaše celá e-mailová adresa',
        helpPassword: 'Heslo',
        helpPasswordBody: 'heslo ke schránce nebo heslo pro aplikaci',
        helpFolder: 'Složka',
        helpFolderBody: 'INBOX (pokud poskytovatel neuvádí jinak)',
        cancel: 'Zrušit',
        connect: 'Připojit',
        promptEmail: 'E-mail účtu IMAP:',
        promptHost: 'Hostitel IMAP (např. imap.example.com):',
        promptPort: 'Port IMAP:',
        promptUsername: 'Uživatelské jméno IMAP:',
        promptPassword: 'Heslo IMAP (nebo heslo pro aplikaci):',
        promptFolder: 'Složka IMAP:',
        promptUseSsl: 'Použít SSL? ano/ne',
        validationEmailRequired: 'E-mailová adresa je povinná.',
        validationHostRequired: 'Hostitel IMAP je povinný.',
        validationPortRange: 'Port IMAP musí být v rozmezí 1 až 65535.',
        validationUsernameRequired: 'Uživatelské jméno IMAP je povinné.',
        validationPasswordRequired: 'Heslo IMAP je povinné.',
      },
      composeUi: {
        replyComposition: 'Psaní odpovědi',
        sendKind: 'Typ odeslání',
        sendKindReply: 'Odpověď',
        sendKindNew: 'Nový e-mail',
        composerTitleReply: 'Odpověď',
        composerTitleNew: 'Nový e-mail',
        sendKindReplyTooltip: 'Odeslat jako odpověď na poslední e-mail v tomto vlákně.',
        sendKindNewTooltip: 'Připravit nový odchozí e-mail mimo toto vlákno.',
        to: 'Komu:',
        subject: 'Předmět:',
        copyDraft: 'Kopírovat koncept',
        draftHistory: 'Historie konceptu',
        draftBodyPlaceholder: 'Začněte psát nebo klikněte na tlačítko Vygenerovat.',
        draftBodyPlaceholderManual: 'Začněte psát svou odpověď.',
        updatedAt: 'Aktualizováno v --:--',
        updatedAtValue: 'Aktualizováno v {time}',
        regenerate: 'Vygenerovat znovu',
        polish: 'Vylepšit text',
        undo: 'Zpět',
        generateDraft: 'Vygenerovat koncept',
        generateQuoteDraft: 'Vygenerovat nabídku',
        quoteOutput: 'Výstup nabídky',
        quoteOutputLabel: 'Zahrnout do odpovědi',
        quoteOutputEmail: 'Nabídka v e-mailu',
        quoteOutputDocument: 'PDF dokument',
        quoteDocumentSettings: 'Nastavení dokumentu',
        quoteOutputEmailTooltip: 'Vloží zkontrolovanou kalkulaci přímo do těla e-mailu.',
        quoteOutputDocumentTooltip: 'Přiloží brandovanou PDF nabídku vygenerovanou ze zkontrolované kalkulace. Při zapnutí obou možností zůstane nabídka v e-mailu a PDF se přiloží.',
        quoteDocumentReady: 'PDF nabídka je připravena',
        quoteDocumentStale: 'PDF nabídku je potřeba aktualizovat',
        quoteDocumentReviewedMeta: 'ze zkontrolované kalkulace',
        quoteDocumentStaleMeta: 'kalkulace se změnila',
        regenerateDocument: 'Aktualizovat PDF',
        quoteDocumentGenerateFailed: 'PDF nabídky se nepodařilo vygenerovat.',
        quoteDocumentRequired: 'Před odesláním vygenerujte PDF nabídky.',
        calculationReviewRequired: 'Před vygenerováním nabídky dokončete a zkontrolujte kalkulační náhled.',
        generateClarificationDraft: 'Vygenerovat dotaz',
        generateWithNotes: 'Vygenerovat s poznámkou',
        selectClarifyingQuestions: 'Vyberte body k upřesnění',
        sending: 'Odesílá se...',
        openProvider: 'Otevřít v Gmailu/Outlooku',
        processRfq: 'Zpracovat poptávku',
        processing: 'Zpracovává se...',
        copyEmailText: 'Kopírovat text e-mailu',
        clarifyTooltip: 'Připravit navazující e-mail s vybranými body k upřesnění.',
        quoteTooltip: 'Připravit přímou odpověď s nabídkou.',
        notesPlaceholder: 'Napište, co chcete doplnit – AI asistent to zapracuje do odpovědi.',
        clarificationPoints: 'Body k upřesnění',
        selectAll: 'Vybrat vše',
        deselectAll: 'Zrušit výběr',
        selectedCount: 'Vybráno: 0',
        selectedCountValue: 'Vybráno: {count}',
        clarificationPlaceholder: 'Zde se zobrazí body k upřesnění.',
        showMoreClarifications: 'Zobrazit více bodů',
        showLessClarifications: 'Zobrazit méně bodů',
        clarifyHintChecking: 'Kontroluji potřebu upřesnění...',
        clarifyHintUnavailable: 'Vyhodnocení upřesnění není k dispozici',
        clarifyHintLikelyHigh: 'Upřesnění pravděpodobně bude potřeba (vysoká)',
        clarifyHintPossibleMedium: 'Upřesnění může být potřeba (střední)',
        clarifyHintUnlikelyLow: 'Upřesnění spíše nebude potřeba (nízká)',
        clarifyHintNotNeeded: 'Upřesnění není potřeba',
        clarifyHintLikely: 'Upřesnění je pravděpodobně potřeba',
        diffHintGeneratedWithQuestions: 'Vygenerován koncept se sekcí Dotazy.',
        diffHintGeneratedNewDraft: '',
        diffHintAddedQuestionsSection: 'Byla přidána sekce Dotazy.',
        diffHintPolishedDraft: 'Koncept byl jazykově upraven.',
        diffHintUpdatedDraft: 'Koncept byl aktualizován.',
        diffHintRestoredPreviousVersion: 'Byla obnovena předchozí verze.',
        sendDisabledStartTyping: 'Začněte psát, aby bylo možné odeslat zprávu.',
        sendDisabledAddRecipient: 'Do pole Komu zadejte alespoň jednu platnou e-mailovou adresu.',
        generateDisabledClarify: 'Vyberte body k upřesnění nebo přidejte poznámku, aby bylo možné generovat.',
        generateDisabledQuote: 'Přidejte poznámku nebo vyberte body k upřesnění, aby bylo možné generovat.',
      },
      rfq: {
        replySubjectFallback: 'Re: Vaše poptávka',
        loadedFromCache: 'Načteno z mezipaměti',
        processed: 'Zpracováno',
        processedBy: 'zpracoval {name}',
        processedAgo: 'Zpracováno {timeAgo}',
        noAttachmentsToProcess: 'Tento e-mail neobsahuje přílohy ke zpracování.',
        processingFollowup: 'Zpracovává se navazující zpráva…',
        followupUpdated: 'Navazující zpráva byla aktualizována',
        followupFailed: 'Zpracování navazující zprávy se nezdařilo. Zkontrolujte poslední změny ve vlákně.',
        processingRfq: 'Zpracovává se poptávka…',
        done: 'Hotovo',
        processingFailed: 'Zpracování se nezdařilo',
        waitingForAutoprocess: 'Čeká se na automatické zpracování…',
        reviewModeManual: 'Režim kontroly je ruční. Pro pokračování označte zprávu jako poptávku.',
        selectMessageFirst: 'Nejprve vyberte zprávu.',
        messageIdMissing: 'Nepodařilo se určit ID zprávy.',
      },
      common: {
        retry: 'Zkusit znovu',
        copyFailed: 'Kopírování se nezdařilo',
        saved: 'Uloženo',
        clear: 'Vymazat',
        close: 'Zavřít',
        edit: 'Upravit',
        open: 'Otevřít',
        download: 'Stáhnout',
        remove: 'Odebrat',
        link: 'Přiřadit',
        unhide: 'Zobrazit znovu',
        adminOnly: 'Pouze pro administrátora',
        dismissNotification: 'Zavřít oznámení',
        pending: 'Čeká na zpracování',
        notAvailable: 'Není k dispozici',
        localeLabels: {
          en: 'Angličtina',
          cs: 'Čeština',
        },
      },
      priority: {
        bucketNormal: 'Standardní',
        bucketHigh: 'Vysoká',
        bucketUrgent: 'Urgentní',
        unavailable: 'Není k dispozici',
        updatedJustNow: 'Aktualizováno právě teď',
        updatedMinutesAgo: 'Aktualizováno před {count} min',
        updatedHourAgo: 'Aktualizováno před 1 h',
        updatedHoursAgo: 'Aktualizováno před {count} h',
        scoreAria: 'Skóre priority: {score} ({label})',
        scoreAriaUnavailable: 'Skóre priority: — ({label})',
        statusUnavailable: 'Skóre není k dispozici',
        noteHigher: 'Tato poptávka má vyšší prioritu než ostatní.',
        noteLower: 'Jiné poptávky mohou mít vyšší prioritu.',
        noteSimilar: 'Tato poptávka má podobnou prioritu jako ostatní.',
      },
      intent: {
        labelUrgent: 'Naléhavé',
        labelHighValue: 'Vysoký potenciál',
        labelRoutine: 'Standardní',
        labelLowInfo: 'Chybí kontext',
      },
      priorityReasons: {
        buyerVip: 'VIP kupující ({buyer})',
        buyerPreferred: 'Preferovaný kupující ({buyer})',
        orderLarge: 'Velká objednávka ({lines} položek, prům. množství ≈ {avg_qty})',
        orderMedium: 'Střední objednávka ({lines} položek, prům. množství ≈ {avg_qty})',
        duePast: 'Termín již uplynul',
        dueVerySoon: 'Termín je velmi blízko',
        dueWithinWeek: 'Termín je do týdne',
        ageAging: 'Poptávka čeká ve schránce delší dobu',
        ageWaiting: 'Poptávka už nějakou dobu čeká',
        intentClassified: 'Profil poptávky: {label}',
        fitPremiumMaterials: 'Obsahuje prémiové materiály',
        clarityImportantMissing: 'Chybí důležité údaje (množství/materiál/termín)',
        claritySomeMissing: 'Chybí některé údaje RFQ',
        boostFresh: 'Zvýhodnění nové poptávky',
        boostOverdue: 'Zvýšení naléhavosti kvůli prošlému termínu',
      },
      compare: {
        onlyDifferences: 'Pouze rozdíly',
        prevDiff: 'Předchozí rozdíl',
        nextDiff: 'Další rozdíl',
        swap: 'Prohodit',
        loadMore: 'Načíst další',
      },
      progress: {
        kpiDialogTitle: 'Aktualizovat stav?',
        kpiDialogBody: 'Označení této poptávky jako Odesláno ovlivní KPI metriky (odeslané nabídky, míru výher a výsledků) ve zvolených časových oknech. V přehledech to nemusí být možné vrátit zpět.',
        unassigned: 'Nepřiřazeno',
        teammate: 'Kolega',
        assignee: 'Řešitel',
        outcome: 'Výsledek',
      },
    },
  };

  const RFQ_TABLE_COPY = {
    en: {
      badges: {
        mergedTooltip: 'Multiple source rows merged',
        mergedLabelOne: 'Merged from {count} source',
        mergedLabelOther: 'Merged from {count} sources',
      },
      columns: {
        unmapped: '— Unmapped —',
        partNumber: 'Part #',
        description: 'Description',
        manufacturer: 'Manufacturer',
        quantity: 'Qty',
        uom: 'UoM',
        needBy: 'Need by',
        needByDate: 'Need-by date',
        currency: 'Currency',
        sourceFile: 'Source file',
        sources: 'Sources',
        partNoShort: 'Part no.',
        partName: 'Part name',
      },
      summary: {
        noTableDetected: 'No table detected yet.',
        rowsOne: '{count} row',
        rowsOther: '{count} rows',
        parsedPrefix: 'Parsed',
        lastPartialResults: 'Showing partial results{suffix}. Download for the full set.',
        partialReasonPrefix: ' due to ',
        partialReasonFallback: ' due to row limits',
        reasonProcessingTimeLimits: 'processing time limits',
        reasonRowLimits: 'row limits',
        reasonPageLimits: 'page limits',
      },
      mapping: {
        manual: 'Manual',
        auto: 'Auto',
        unmapped: 'Unmapped',
        override: 'Override',
        noAttachmentMetadata: 'No attachment metadata available.',
        attachmentPrefix: 'Attachment',
        tablePage: 'Table {index} · Page {page}',
        allRowsMapped: 'All rows mapped',
        rowsMapped: '{parsed} of {source} rows mapped',
        saveSuccess: 'Saved mapping override for {name}.',
        saveFailed: 'Failed to save mapping override.',
        reviewRequired: 'Automatic mapping requires manual review. Fix the column mapping manually.',
        rejectedTemplate: 'Automatic mapping was rejected ({status}).{trace}{reason} Fix the column mapping manually.',
        traceTemplate: ' Trace {traceId}.',
        reasonTemplate: ' Reason: {reason}',
      },
      export: {
        unavailable: 'Export temporarily unavailable. Please reload the page.',
        sheetJsRequired: 'Export requires SheetJS support.',
        noRows: 'No table rows available for export.',
        validationFailed: 'Export validation failed.',
        writeFileUnavailable: 'SheetJS writeFile API unavailable.',
        workbookTitle: 'RFQ Table {rfqId}',
      },
      warnings: {
        pdfEngineUnavailable: 'PDF table engine unavailable',
        pdfParsingTimedOut: 'PDF parsing timed out',
        pdfParsingTruncated: 'PDF parsing truncated',
        pdfParsingTruncatedWithReason: 'PDF parsing truncated ({reason})',
      },
      attachmentsCadHeading: 'CAD attachments',
      attachmentsCadEmpty: 'No CAD attachments to display.',
      attachmentsCadLabel: 'CAD',
      attachmentsCadNoMetadata: 'No quote-relevant CAD metadata extracted yet.',
      attachmentsCadMissingLabel: 'Missing to quote',
      attachmentsCadFallbackNote: 'Created one CAD fallback row from grouped attachments.',
      attachmentsCadStatus: {
        success: 'CAD analyzed',
        partial: 'CAD partial',
        failed: 'CAD failed',
        timeout: 'CAD timed out',
      },
      attachmentsDownloadLabel: 'Download',
      attachmentsDownloadUnavailable: 'Download unavailable',
      attachmentsSizeUnknown: 'Size unknown',
      fieldLabels: {
        material: 'Material',
        tolerance: 'Tolerance',
        heat_treatment: 'Heat treatment',
        surface_finish: 'Surface finish',
        process: 'Manufacturing process',
        spec_norm: 'Specification norm',
        spec_dn: 'DN',
        spec_type: 'Specification type',
        files: 'Files',
        notes: 'Notes',
      },
    },
    cs: {
      badges: {
        mergedTooltip: 'Sloučeno z více zdrojových řádků',
        mergedLabelOne: 'Sloučeno z {count} zdroje',
        mergedLabelFew: 'Sloučeno ze {count} zdrojů',
        mergedLabelOther: 'Sloučeno z {count} zdrojů',
      },
      columns: {
        unmapped: '— Nepřiřazeno —',
        partNumber: 'Díl',
        description: 'Popis',
        manufacturer: 'Výrobce',
        quantity: 'Množství',
        uom: 'MJ',
        needBy: 'Termín dodání',
        needByDate: 'Termín dodání',
        currency: 'Měna',
        sourceFile: 'Zdrojový soubor',
        sources: 'Zdroje',
        partNoShort: 'Číslo dílu',
        partName: 'Název dílu',
      },
      summary: {
        noTableDetected: 'Tabulka poptávky zatím nebyla rozpoznána.',
        rowsOne: '{count} řádek',
        rowsFew: '{count} řádky',
        rowsOther: '{count} řádků',
        parsedPrefix: 'Extrahováno',
        lastPartialResults: 'Zobrazeny jsou částečné výsledky{suffix}. Kompletní data stáhněte v exportu.',
        partialReasonPrefix: ' kvůli ',
        partialReasonFallback: ' kvůli omezení počtu řádků',
        reasonProcessingTimeLimits: 'časovým limitům zpracování',
        reasonRowLimits: 'omezení počtu řádků',
        reasonPageLimits: 'omezení počtu stran',
      },
      mapping: {
        manual: 'Ručně',
        auto: 'Automaticky',
        unmapped: 'Nepřiřazeno',
        override: 'Ruční mapování',
        noAttachmentMetadata: 'Metadata příloh nejsou k dispozici.',
        attachmentPrefix: 'Příloha',
        tablePage: 'Tabulka {index} · Strana {page}',
        allRowsMapped: 'Všechny řádky jsou namapované',
        rowsMapped: 'Namapováno {parsed} z {source} řádků',
        saveSuccess: 'Ruční mapování pro {name} bylo uloženo.',
        saveFailed: 'Ruční mapování se nepodařilo uložit.',
        reviewRequired: 'Automatické mapování vyžaduje ruční kontrolu. Upravte mapování sloupců ručně.',
        rejectedTemplate: 'Automatické mapování bylo odmítnuto ({status}).{trace}{reason} Upravte mapování sloupců ručně.',
        traceTemplate: ' Trace {traceId}.',
        reasonTemplate: ' Důvod: {reason}',
      },
      export: {
        unavailable: 'Export je dočasně nedostupný. Obnovte prosím stránku.',
        sheetJsRequired: 'Export vyžaduje podporu SheetJS.',
        noRows: 'Pro export nejsou k dispozici žádné řádky tabulky.',
        validationFailed: 'Kontrola exportu se nezdařila.',
        writeFileUnavailable: 'Rozhraní SheetJS writeFile není k dispozici.',
        workbookTitle: 'Tabulka poptávky {rfqId}',
      },
      warnings: {
        pdfEngineUnavailable: 'Modul pro tabulky z PDF není k dispozici',
        pdfParsingTimedOut: 'Zpracování PDF vypršelo',
        pdfParsingTruncated: 'Zpracování PDF bylo zkráceno',
        pdfParsingTruncatedWithReason: 'Zpracování PDF bylo zkráceno ({reason})',
      },
      attachmentsCadHeading: 'CAD přílohy',
      attachmentsCadEmpty: 'Žádné CAD přílohy k zobrazení.',
      attachmentsCadLabel: 'CAD',
      attachmentsCadNoMetadata: 'Zatím nebyla extrahována žádná cenově relevantní CAD metadata.',
      attachmentsCadMissingLabel: 'Chybí pro nabídku',
      attachmentsCadFallbackNote: 'Z propojených CAD příloh byl vytvořen jeden náhradní řádek.',
      attachmentsCadStatus: {
        success: 'CAD analyzováno',
        partial: 'CAD částečné',
        failed: 'CAD selhalo',
        timeout: 'Čas CAD vypršel',
      },
      attachmentsDownloadLabel: 'Stáhnout',
      attachmentsDownloadUnavailable: 'Stažení není k dispozici',
      attachmentsSizeUnknown: 'Neznámá velikost',
      fieldLabels: {
        material: 'Materiál',
        tolerance: 'Tolerance',
        heat_treatment: 'Tepelné zpracování',
        surface_finish: 'Povrchová úprava',
        process: 'Výrobní proces',
        spec_norm: 'Norma',
        spec_dn: 'DN',
        spec_type: 'Typ specifikace',
        files: 'Soubory',
        notes: 'Poznámky',
      },
    },
    de: {
      attachmentsCadHeading: 'CAD-Anhänge',
      attachmentsCadEmpty: 'Keine CAD-Anhänge zum Anzeigen.',
      attachmentsCadLabel: 'CAD',
      attachmentsCadNoMetadata: 'Es wurden noch keine angebotsrelevanten CAD-Metadaten extrahiert.',
      attachmentsCadMissingLabel: 'Fehlt für das Angebot',
      attachmentsCadFallbackNote: 'Aus gruppierten CAD-Anhängen wurde eine Fallback-Zeile erstellt.',
      attachmentsCadStatus: {
        success: 'CAD analysiert',
        partial: 'CAD teilweise',
        failed: 'CAD fehlgeschlagen',
        timeout: 'CAD-Zeitlimit',
      },
      attachmentsDownloadLabel: 'Herunterladen',
      attachmentsDownloadUnavailable: 'Download nicht verfügbar',
      attachmentsSizeUnknown: 'Unbekannte Größe',
      fieldLabels: {
        material: 'Material',
        tolerance: 'Toleranz',
        heat_treatment: 'Wärmebehandlung',
        surface_finish: 'Oberflächengüte',
        process: 'Prozess',
        spec_norm: 'Norm',
        spec_dn: 'DN',
        spec_type: 'Normtyp',
        files: 'Dateien',
        notes: 'Notizen',
      },
    },
  };

  const STATS_TILE_COPY = {
    en: {
      title: 'Workspace stats',
      updated: 'Updated {time}',
      refresh: 'Refresh',
      expandStats: 'Expand stats',
      expandTitle: 'Expand',
      summaryWindowAria: 'Summary window',
      tierLabel: 'Tier {tier}',
      tierCurrent: "You're on Tier {tier}.",
      tierMax: "You've unlocked all current insights. More insights coming soon.",
      tierNext: 'Next tier unlocks in ~{rfqs} RFQs and {quotes} quotes.',
      timeWindows: {
        '24h': '24h',
        '7d': '7d',
        '30d': '30d',
      },
      tabs: {
        performance: 'Performance',
        overview: 'Overview',
        latency: 'Latency',
        buyers: 'Buyers & materials',
        quality: 'Quality',
        readiness: 'Readiness',
      },
      tier2Tabs: {
        dataQuality: 'Data quality',
        teamActivity: 'Team activity',
      },
      kpiPlaceholderTitle: 'Key metrics coming soon',
      kpiPlaceholderBody: 'We are preparing expanded workspace insights for this tier.',
      kpiRegionLabel: 'Workspace key metrics',
      emptyMessage: 'Workspace stats will appear soon.',
      kpiLabels: {
        rfqsProcessed: 'Processed',
        quotesSent: 'Quoted',
        responseRatio: 'Response ratio',
        winRatio: 'Win rate',
        outcomeRate: 'Outcome rate',
        quotesWon: 'Won',
        quotesLost: 'Lost',
        pipelineNew: 'New',
        pipelineInProgress: 'In progress',
        quotesPending: 'Awaiting outcome',
        medianQuoteTurnaround: 'Quote time',
        medianFirstResponseTime: 'First response time',
        avgCompleteness: 'Completeness',
      },
      kpiTooltips: {
        rfqsProcessed: 'RFQs processed in the selected window.',
        lifecycleWon: 'RFQs marked as Won in the selected window.',
        lifecycleLost: 'RFQs marked as Lost in the selected window.',
        quotesSent: 'RFQs that were quoted in the selected window.',
        quotesPending: 'Quotes with no outcome yet.',
        pipelineNew: 'RFQs received in the selected window.',
        pipelineInProgress: 'RFQs currently being worked on.',
        response_ratio: 'Percent of RFQs that received at least one quote in this window.',
        responseRatio: 'Percent of RFQs that received at least one quote in this window.',
        winRatio: 'Percentage of decided quotes that were won. Based on Wins ÷ (Wins + Losses); pending quotes not included.',
        outcomeRate: 'Percentage of sent quotes with an outcome (won or lost).',
        outcomeRatePending: 'Percentage of sent quotes with an outcome (won or lost). {pending_count} quotes still pending.',
        medianQuoteTurnaround: 'Median time from RFQ received to quote sent.',
        quoteTime: 'Median time from RFQ received to quote sent.',
        medianFirstResponseTime: 'Median time from RFQ received to first outbound response (clarification or quote).',
        firstResponseTime: 'Median time from RFQ received to first outbound response (clarification or quote).',
        avgCompleteness: 'Average field completeness of processed RFQs.',
      },
      csvButtons: {
        summary: 'Download summary CSV',
        detail: 'Download detail CSV',
      },
      attachmentsCard: {
        title: 'Attachment mix',
        helperEmpty: 'No attachments processed during this window.',
        helperInsufficient: 'Need more attachment variety to chart a mix.',
        otherLabel: 'Other',
        legendTemplate: '{label} · {percent} ({count})',
        labels: {
          pdf: 'PDF',
          docx: 'DOCX',
          xlsx: 'XLSX',
          csv: 'CSV',
          cad: 'CAD',
          zip: 'ZIP',
        },
      },
      notesCard: {
        title: 'Notes & clarifications',
        notesLabel: 'Shared notes',
        notes: '{filled} of {total} RFQs with shared notes',
        clarifyingLabel: 'Clarifying questions',
        clarifying: '{count} clarifying questions proposed',
        clarifyingNeeded: '{needed} RFQs needed clarifying',
        clarifyingNotNeeded: '{notNeeded} RFQs skipped clarifying',
      },
      performance: {
        heading: 'Performance insights',
        csvDisabled: 'CSV export coming soon',
        rfqsQuotes: {
          heading: 'RFQs vs quotes',
          csvLabel: 'Download RFQ vs quote CSV',
          helperEmpty: 'RFQ and quote trends will appear once data is available.',
          helperInsufficient: 'Need more RFQ and quote activity to render this chart.',
          placeholderTitle: 'RFQ vs quote chart coming soon',
          placeholderBody: 'We are preparing this chart to visualize RFQs and quotes.',
          chartAria: 'RFQs versus quotes over time',
        },
        latencyTrend: {
          heading: 'Quote latency trend',
          csvLabel: 'Download latency CSV',
          helperEmpty: 'Latency trends will appear once enough RFQs are quoted.',
          helperInsufficient: 'Need more consistent latency samples to render this chart.',
          placeholderTitle: 'Latency chart coming soon',
          placeholderBody: 'We are preparing this visualization of latency trends.',
          chartAria: 'Quote latency trend',
        },
        cacheHit: {
          heading: 'Cache efficiency',
          csvLabel: 'Download cache CSV',
          helperEmpty: 'Cache insights will appear after more activity.',
          helperInsufficient: 'Need more cache events to render detailed insights.',
          placeholderTitle: 'Cache insights coming soon',
          placeholderBody: 'We are preparing cache hit and miss summaries for this workspace.',
          chartAria: 'Cache hit summary',
        },
        csvHeaders: {
          rfqsQuotes: {
            granularity: 'Granularity',
            bucketStart: 'Bucket start',
            rfqs: 'RFQs',
            quotes: 'Quotes',
          },
          latencyTrend: {
            recordedAt: 'Recorded at',
            quoteLatency: 'Quote latency',
            rfqToQuoteLatency: 'RFQ→quote latency',
            timeToTable: 'Time to table',
            extractionLatency: 'Extraction latency',
          },
          cacheHit: {
            section: 'Section',
            label: 'Label',
            value: 'Value',
            summarySection: 'Summary',
            timelineSection: 'Timeline',
            hits: 'Hits',
            misses: 'Misses',
            hitRate: 'Hit rate',
          },
        },
        cacheHitStatus: {
          hit: 'Hit',
          miss: 'Miss',
        },
      },
      dataQuality: {
        title: 'Data quality',
        csvLabel: 'Download data quality CSV',
        completenessHeading: 'Field completeness',
        completenessEmpty: 'Need more RFQs to chart field completeness.',
        missingHeading: 'Top missing fields',
        topGapsHeading: 'Top gaps',
        topGapsInfoAria: 'About top gaps',
        missingEmpty: 'Great work—no high-signal missing fields detected.',
        missingRateTemplate: '{percent}% missing',
        attachmentsHeading: 'Attachment mix',
        attachmentsEmpty: 'No attachment mix available for this window.',
        attachmentsInfoAria: 'About attachment mix',
        attachmentsTooltipHeading: 'What this shows',
        attachmentsTooltipBody: 'Breakdown of attachment types seen in RFQs in this time window.',
        attachmentsPeriodEmpty: 'No attachments in this period',
        buyersHeading: 'Buyer mix',
        buyersEmpty: 'Buyer engagement insights will appear after more activity.',
        readinessTitle: 'Quote readiness',
        readinessSubtitle: 'Percent of recent RFQs meeting key fields',
        readinessHelperTemplate: '{ready} of {total} RFQs met the key fields. Top gaps: {fields}.',
        readinessHelperNoGaps: 'All key fields are covered.',
        readinessHelperEmpty: 'Need more RFQs to assess quote readiness.',
        readinessInlineTemplate: '{ready} of {total} RFQs ready for quoting',
        readinessInfoAria: 'What defines quote readiness',
        tooltipHeading: 'What this shows',
        tooltipIntro: 'An RFQ is considered ready for quoting when all of these fields are filled:',
        tooltipFootnote: 'The required fields may vary depending on your workspace configuration.',
        readinessTooltipLabel: 'View readiness key fields',
        readinessTooltipEmpty: 'Key fields will appear once readiness data is available.',
        readinessValueEmpty: '--',
        gapsTooltipHeading: 'What this shows',
        gapsTooltipBody1: 'These are the fields most often missing in recent RFQs.',
        gapsTooltipBody2: 'Each bar shows the percentage of RFQs where that field was missing within the selected time range.',
      },
      teamActivity: {
        title: 'Team activity',
        csvLabel: 'Download team activity CSV',
        leaderboardHeading: 'Top teammates',
        topTeammateHeading: 'Top teammate',
        teammateInfoAria: 'About top teammate',
        teammateTooltipHeading: 'What this shows',
        teammateTooltipBody1: 'The teammate with the most RFQs processed in the selected time range, plus their activity.',
        teammateTooltipBody2: 'Response ratio = percentage of RFQs this teammate turned into quotes.',
        teammateFallback: 'Teammate',
        leaderboardEmpty: 'Team activity appears once teammates process RFQs.',
        leaderboardRfqsLabel: 'RFQs processed',
        leaderboardNotesLabel: 'Notes shared',
        leaderboardQuotesLabel: 'Quotes sent',
        leaderboardResponseLabel: 'Response ratio',
        clarificationsHeading: 'Clarifications',
        clarificationsInfoAria: 'About clarifications',
        clarificationsTooltipHeading: 'What this shows',
        clarificationsTooltipBody: 'How many RFQs in this period needed extra clarification before you could quote.',
        clarificationsNeededLabel: 'Needed',
        clarificationsNotNeededLabel: 'Not needed',
        clarificationsQuestionsLabel: 'Questions',
        clarificationsNeededText: 'needed',
        clarificationsNotNeededText: 'not needed',
        clarificationsQuestionsText: 'questions',
        noRfqsPeriod: 'No RFQs in this period',
        clarificationsEmpty: 'Clarification insights will appear after more activity.',
        sparklineHeading: 'Recent RFQ activity',
        sparklineInfoAria: 'About recent RFQ activity',
        sparklineTooltipHeading: 'What this shows',
        sparklineTooltipBody1: 'How many RFQs were processed in each time period.',
        sparklineTooltipBody2: 'Bars represent activity within the selected window.',
        asOfLocal: 'As of {time} (local)',
        recentTooNarrow: 'Activity timeline not available on this width.',
        sparklineEmpty: 'RFQ activity sparkline will appear after more processing.',
        quoteSparklineHeading: 'Recent quote activity',
        quoteSparklineInfoAria: 'About recent quote activity',
        quoteSparklineTooltipHeading: 'What this shows',
        quoteSparklineTooltipBody1: 'How many quotes were processed in each time period.',
        quoteSparklineTooltipBody2: 'Bars represent activity within the selected window.',
        quoteSparklineEmpty: 'Quote activity sparkline will appear after more quotes are sent.',
        sparklineRfqsLabel: 'RFQs',
        sparklineNotesLabel: 'Notes',
      },
      buyersMaterials: {
        heading: 'Buyers & materials',
        helper: 'Monitor buyer engagement, materials mix, and latency performance.',
        buyersHeading: 'Top buyers',
        buyersCsvLabel: 'Download buyers CSV',
        buyersEmpty: 'Buyer engagement insights will appear after more activity.',
        buyersTruncated: 'Showing top buyers due to volume.',
        rfqsLabel: 'RFQs',
        quotesLabel: 'Quotes',
        trendUpLabel: 'Trending up',
        trendDownLabel: 'Trending down',
        trendFlatLabel: 'Stable performance',
        anonymousBuyerFallback: 'Buyer',
        anonymousBuyerTemplate: 'Buyer · {hash}',
        materialsHeading: 'Materials mix',
        materialsCsvLabel: 'Download materials CSV',
        materialsEmpty: 'Materials mix will appear after more activity.',
        materialsTruncated: 'Showing top materials due to volume.',
        materialRfqsLabel: 'RFQs',
        materialQtyLabel: 'Total qty',
        materialFallback: 'Material',
        latencyHeading: 'Quote latency by buyer',
        latencyCsvLabel: 'Download latency CSV',
        latencyEmpty: 'Latency insights will appear once enough quotes are processed.',
        latencyTruncated: 'Showing top buyers due to volume.',
        latencyColumns: {
          buyer: 'Buyer',
          rfqs: 'RFQs',
          quotes: 'Quotes',
          response: 'Response',
          timeToTable: 'Time to table',
          quoteLatency: 'Quote latency',
          rfqToQuoteLatency: 'RFQ→quote',
          lastSeen: 'Last seen',
        },
        csvDisabled: 'CSV export coming soon',
        csvEnabled: 'Download CSV',
      },
      readiness: {
        title: 'Readiness',
        helperReady: 'Workspace meets the requirements for auto-drafts.',
        helperInProgress: 'Complete the checklist items to unlock auto-drafts.',
        helperInsufficient: 'Need more data to assess readiness.',
        badgeReady: 'Ready',
        badgeInProgress: 'In progress',
        helpLabel: 'How readiness works',
        checklistHeading: 'Readiness checklist',
        checklistEmpty: 'Need more signals to evaluate readiness.',
        currentLabel: 'Current: {value}',
        targetLabel: 'Target: {value}',
        samplesLabel: 'Samples',
        autoDraftHeading: 'Auto-draft eligibility',
        autoDraftEligible: 'Auto-drafts are enabled for this workspace.',
        autoDraftIneligible: 'Auto-drafts unlock once the checklist is complete.',
        bottlenecksHeading: 'Top bottlenecks',
        bottlenecksEmpty: 'No bottlenecks detected for this window.',
        csvChecklistLabel: 'Download checklist CSV',
        csvBottlenecksLabel: 'Download bottlenecks CSV',
        tooltipTitle: 'About readiness',
        tooltipBody: 'Readiness highlights the requirements to unlock auto-drafts and spot remaining gaps.',
        tooltipDismiss: 'Close',
        latencyChipTemplate: '{value} · target {target}',
        fieldChipTemplate: '{value} · target {target} · {missing} missing',
        csvHeaders: {
          checklist: {
            item: 'Checklist item',
            status: 'Status',
            current: 'Current value',
            target: 'Target value',
            samples: 'Samples',
            unit: 'Unit',
          },
          bottlenecks: {
            label: 'Bottleneck',
            metric: 'Metric',
            category: 'Category',
            severity: 'Severity',
            samples: 'Samples',
            value: 'Observed value',
            target: 'Target value',
            missing: 'Missing',
            total: 'Total',
          },
        },
        csvStatusLabels: {
          ready: 'Ready',
          inProgress: 'In progress',
        },
        csvUnitLabels: {
          percent: 'Percent',
          count: 'Count',
        },
        csvCategoryLabels: {
          latency: 'Latency',
          field: 'Field coverage',
        },
        csvSeverityLabels: {
          critical: 'Critical',
          warning: 'Warning',
          info: 'Info',
        },
      },
    },
    cs: {
      title: 'Statistiky pracovního prostoru',
      updated: 'Aktualizováno {time}',
      refresh: 'Obnovit',
      expandStats: 'Rozbalit statistiky',
      expandTitle: 'Rozbalit',
      summaryWindowAria: 'Časové okno souhrnu',
      tierLabel: 'Úroveň {tier}',
      tierCurrent: 'Jste na úrovni {tier}.',
      tierMax: 'Odemkli jste všechny aktuální přehledy. Další brzy přibudou.',
      tierNext: 'Další úroveň se odemkne přibližně za {rfqs} poptávek a {quotes} nabídek.',
      timeWindows: {
        '24h': '24 h',
        '7d': '7 dní',
        '30d': '30 dní',
      },
      tabs: {
        performance: 'Výkon',
        overview: 'Přehled',
        latency: 'Latence',
        buyers: 'Zákazníci a materiály',
        quality: 'Kvalita',
        readiness: 'Připravenost',
      },
      tier2Tabs: {
        dataQuality: 'Kvalita dat',
        teamActivity: 'Aktivita týmu',
      },
      kpiPlaceholderTitle: 'Klíčové metriky již brzy',
      kpiPlaceholderBody: 'Pro tento stupeň připravujeme rozšířené přehledy.',
      kpiRegionLabel: 'Klíčové metriky pracovního prostoru',
      emptyMessage: 'Statistiky pracovního prostoru budou brzy k dispozici.',
      kpiLabels: {
        rfqsProcessed: 'Zpracované poptávky',
        quotesSent: 'Odeslané nabídky',
        responseRatio: 'Míra odpovědí',
        winRatio: 'Vítězná míra',
        quotesWon: 'Vyhrané',
        quotesLost: 'Prohrané',
        pipelineNew: 'Nové',
        pipelineInProgress: 'V řešení',
        quotesPending: 'Čeká na výsledek',
        medianQuoteTurnaround: 'Doba do nabídky',
        medianFirstResponseTime: 'Doba do první odpovědi',
        avgCompleteness: 'Průměrná úplnost',
      },
      kpiTooltips: {
        rfqsProcessed: 'Poptávky zpracované ve vybraném období.',
        lifecycleWon: 'Poptávky označené ve vybraném období jako vyhrané.',
        lifecycleLost: 'Poptávky označené ve vybraném období jako ztracené.',
        quotesSent: 'Poptávky, ke kterým byla ve vybraném období odeslána nabídka.',
        quotesPending: 'Nabídky, které zatím nemají výsledek.',
        pipelineNew: 'Poptávky přijaté ve vybraném období.',
        pipelineInProgress: 'Poptávky, které jsou právě v řešení.',
        response_ratio: 'Podíl poptávek, které v tomto období dostaly alespoň jednu nabídku.',
        responseRatio: 'Podíl poptávek, které v tomto období dostaly alespoň jednu nabídku.',
        winRatio: 'Podíl rozhodnutých nabídek, které byly vyhrané. Počítá se jako výhry ÷ (výhry + prohry); čekající nabídky se nezapočítávají.',
        outcomeRate: 'Podíl odeslaných nabídek, které mají výsledek (vyhráno nebo ztraceno).',
        outcomeRatePending: 'Podíl odeslaných nabídek, které mají výsledek (vyhráno nebo ztraceno). {pending_count} nabídek stále čeká na výsledek.',
        medianQuoteTurnaround: 'Medián času od přijetí poptávky po odeslání nabídky.',
        quoteTime: 'Medián času od přijetí poptávky po odeslání nabídky.',
        medianFirstResponseTime: 'Medián času od přijetí poptávky po první odchozí reakci (dotaz nebo nabídka).',
        firstResponseTime: 'Medián času od přijetí poptávky po první odchozí reakci (dotaz nebo nabídka).',
        avgCompleteness: 'Průměrná úplnost polí u zpracovaných poptávek.',
      },
      csvButtons: {
        summary: 'Stáhnout souhrn CSV',
        detail: 'Stáhnout detail CSV',
      },
      attachmentsCard: {
        title: 'Složení příloh',
        helperEmpty: 'V tomto okně nebyly zpracovány žádné přílohy.',
        helperInsufficient: 'Pro zobrazení složení je potřeba více typů příloh.',
        otherLabel: 'Ostatní',
        legendTemplate: '{label} · {percent} ({count})',
        labels: {
          pdf: 'PDF',
          docx: 'DOCX',
          xlsx: 'XLSX',
          csv: 'CSV',
          cad: 'CAD',
          zip: 'ZIP',
        },
      },
      notesCard: {
        title: 'Poznámky a dotazy',
        notesLabel: 'Sdílené poznámky',
        notes: 'Sdílené poznámky u {filled} z {total} poptávek',
        clarifyingLabel: 'Dotazy k doplnění',
        clarifying: 'Navrženo {count} dotazů k doplnění',
        clarifyingNeeded: 'Nutné u {needed} poptávek',
        clarifyingNotNeeded: 'Bez dotazu u {notNeeded} poptávek',
      },
      performance: {
        heading: 'Přehled výkonu',
        csvDisabled: 'Export CSV bude brzy k dispozici',
        rfqsQuotes: {
          heading: 'Poptávky vs. nabídky',
          csvLabel: 'Stáhnout CSV poptávky vs. nabídky',
          helperEmpty: 'Trendy poptávek a nabídek se zobrazí, jakmile budou k dispozici data.',
          helperInsufficient: 'Pro tento graf potřebujeme více aktivit v poptávkách i nabídkách.',
          placeholderTitle: 'Graf poptávky vs. nabídky již brzy',
          placeholderBody: 'Připravujeme vizualizaci poptávek a nabídek pro tento workspace.',
          chartAria: 'Poptávky a nabídky v čase',
        },
        latencyTrend: {
          heading: 'Trend latence nabídek',
          csvLabel: 'Stáhnout CSV latence',
          helperEmpty: 'Trend latence se zobrazí, jakmile bude dostatek zpracovaných poptávek.',
          helperInsufficient: 'Pro zobrazení grafu potřebujeme více konzistentních vzorků latence.',
          placeholderTitle: 'Graf latence již brzy',
          placeholderBody: 'Připravujeme vizualizaci trendů latence pro tento workspace.',
          chartAria: 'Trend latence nabídek',
        },
        cacheHit: {
          heading: 'Efektivita cache',
          csvLabel: 'Stáhnout CSV cache',
          helperEmpty: 'Přehled cache se zobrazí po další aktivitě.',
          helperInsufficient: 'Pro detailnější přehled potřebujeme více událostí cache.',
          placeholderTitle: 'Přehled cache již brzy',
          placeholderBody: 'Připravujeme souhrny zásahů a chyb cache pro tento workspace.',
          chartAria: 'Souhrn zásahů cache',
        },
        csvHeaders: {
          rfqsQuotes: {
            granularity: 'Granularita',
            bucketStart: 'Začátek segmentu',
            rfqs: 'Poptávky',
            quotes: 'Nabídky',
          },
          latencyTrend: {
            recordedAt: 'Zaznamenáno',
            quoteLatency: 'Latence nabídky',
            rfqToQuoteLatency: 'Latence RFQ→nabídka',
            timeToTable: 'Čas do tabulky',
            extractionLatency: 'Latence extrakce',
          },
          cacheHit: {
            section: 'Sekce',
            label: 'Popisek',
            value: 'Hodnota',
            summarySection: 'Souhrn',
            timelineSection: 'Časová osa',
            hits: 'Zásahy',
            misses: 'Minutí',
            hitRate: 'Úspěšnost zásahů',
          },
        },
        cacheHitStatus: {
          hit: 'Zásah',
          miss: 'Minutí',
        },
      },
      dataQuality: {
        title: 'Kvalita dat',
        csvLabel: 'Stáhnout CSV kvality dat',
        completenessHeading: 'Úplnost polí',
        completenessEmpty: 'Pro zobrazení úplnosti je potřeba více poptávek.',
        missingHeading: 'Nejčastěji chybějící pole',
        topGapsHeading: 'Hlavní mezery',
        topGapsInfoAria: 'O hlavních mezerách',
        missingEmpty: 'Skvělá práce – žádná zásadně chybějící pole.',
        missingRateTemplate: 'chybí {percent} %',
        attachmentsHeading: 'Složení příloh',
        attachmentsEmpty: 'V tomto okně není k dispozici složení příloh.',
        attachmentsInfoAria: 'O složení příloh',
        attachmentsTooltipHeading: 'Co to ukazuje',
        attachmentsTooltipBody: 'Rozložení typů příloh, které se v tomto časovém okně objevily v poptávkách.',
        attachmentsPeriodEmpty: 'V tomto období nejsou žádné přílohy',
        buyersHeading: 'Mix odběratelů',
        buyersEmpty: 'Přehled odběratelů se zobrazí po větší aktivitě.',
        readinessTitle: 'Připravenost nabídek',
        readinessSubtitle: 'Procento nedávných poptávek splňujících klíčová pole',
        readinessHelperTemplate: 'Připraveno {ready} z {total} poptávek. Hlavní mezery: {fields}.',
        readinessHelperNoGaps: 'Všechna klíčová pole jsou pokryta.',
        readinessHelperEmpty: 'Pro vyhodnocení připravenosti je potřeba více poptávek.',
        readinessInlineTemplate: 'Připraveno k nacenění {ready} z {total} poptávek',
        readinessInfoAria: 'Co určuje připravenost nabídky',
        tooltipHeading: 'Co to ukazuje',
        tooltipIntro: 'Poptávka je považována za připravenou k nacenění, pokud jsou vyplněna všechna tato pole:',
        tooltipFootnote: 'Požadovaná pole se mohou lišit podle nastavení workspace.',
        readinessTooltipLabel: 'Zobrazit klíčová pole připravenosti',
        readinessTooltipEmpty: 'Klíčová pole se zobrazí, jakmile budou k dispozici data připravenosti.',
        readinessValueEmpty: '--',
        gapsTooltipHeading: 'Co to ukazuje',
        gapsTooltipBody1: 'Jde o pole, která v nedávných poptávkách chybí nejčastěji.',
        gapsTooltipBody2: 'Každý pruh ukazuje podíl poptávek, ve kterých dané pole v zvoleném období chybělo.',
      },
      teamActivity: {
        title: 'Aktivita týmu',
        csvLabel: 'Stáhnout CSV aktivity týmu',
        leaderboardHeading: 'Nejaktivnější členové',
        topTeammateHeading: 'Nejaktivnější kolega',
        teammateInfoAria: 'O nejaktivnějším kolegovi',
        teammateTooltipHeading: 'Co to ukazuje',
        teammateTooltipBody1: 'Kolega s nejvyšším počtem zpracovaných poptávek ve zvoleném období včetně jeho aktivity.',
        teammateTooltipBody2: 'Míra odpovědí = podíl poptávek, které tento kolega proměnil v nabídky.',
        teammateFallback: 'Kolega',
        leaderboardEmpty: 'Aktivita týmu se zobrazí, jakmile členové zpracují poptávky.',
        leaderboardRfqsLabel: 'Zpracované poptávky',
        leaderboardNotesLabel: 'Sdílené poznámky',
        leaderboardQuotesLabel: 'Odeslané nabídky',
        leaderboardResponseLabel: 'Míra odpovědí',
        clarificationsHeading: 'Upřesnění',
        clarificationsInfoAria: 'O dotazech',
        clarificationsTooltipHeading: 'Co to ukazuje',
        clarificationsTooltipBody: 'Kolik poptávek v tomto období vyžadovalo doplnění před přípravou nabídky.',
        clarificationsNeededLabel: 'Vyžadováno',
        clarificationsNotNeededLabel: 'Nebylo třeba',
        clarificationsQuestionsLabel: 'Otázky',
        clarificationsNeededText: 'vyžadovalo dotaz',
        clarificationsNotNeededText: 'bez dotazu',
        clarificationsQuestionsText: 'otázek',
        noRfqsPeriod: 'V tomto období nejsou žádné poptávky',
        clarificationsEmpty: 'Přehled dotazů se zobrazí po další aktivitě.',
        sparklineHeading: 'Nedávná aktivita poptávek',
        sparklineInfoAria: 'O nedávné aktivitě poptávek',
        sparklineTooltipHeading: 'Co to ukazuje',
        sparklineTooltipBody1: 'Kolik poptávek bylo zpracováno v jednotlivých časových úsecích.',
        sparklineTooltipBody2: 'Sloupce představují aktivitu ve zvoleném okně.',
        asOfLocal: 'K {time} (místní čas)',
        recentTooNarrow: 'Na této šířce nelze časovou osu zobrazit.',
        sparklineEmpty: 'Průběh aktivity se zobrazí po dalších zpracovaných poptávkách.',
        quoteSparklineHeading: 'Nedávná aktivita nabídek',
        quoteSparklineInfoAria: 'O nedávné aktivitě nabídek',
        quoteSparklineTooltipHeading: 'Co to ukazuje',
        quoteSparklineTooltipBody1: 'Kolik nabídek bylo zpracováno v jednotlivých časových úsecích.',
        quoteSparklineTooltipBody2: 'Sloupce představují aktivitu ve zvoleném okně.',
        quoteSparklineEmpty: 'Průběh aktivity nabídek se zobrazí po odeslání dalších nabídek.',
        sparklineRfqsLabel: 'Poptávky',
        sparklineNotesLabel: 'Poznámky',
      },
      buyersMaterials: {
        heading: 'Zákazníci a materiály',
        helper: 'Sledujte zapojení zákazníků, mix materiálů a výkon latencí.',
        buyersHeading: 'Top zákazníci',
        buyersCsvLabel: 'Stáhnout CSV zákazníků',
        buyersEmpty: 'Přehled zákazníků se zobrazí po další aktivitě.',
        buyersTruncated: 'Zobrazuje se omezený seznam zákazníků kvůli objemu.',
        rfqsLabel: 'Poptávky',
        quotesLabel: 'Nabídky',
        trendUpLabel: 'Trend roste',
        trendDownLabel: 'Trend klesá',
        trendFlatLabel: 'Stabilní výkon',
        anonymousBuyerFallback: 'Zákazník',
        anonymousBuyerTemplate: 'Zákazník · {hash}',
        materialsHeading: 'Mix materiálů',
        materialsCsvLabel: 'Stáhnout CSV materiálů',
        materialsEmpty: 'Mix materiálů bude k dispozici po další aktivitě.',
        materialsTruncated: 'Zobrazuje se omezený seznam materiálů kvůli objemu.',
        materialRfqsLabel: 'Poptávky',
        materialQtyLabel: 'Celkové množství',
        materialFallback: 'Materiál',
        latencyHeading: 'Latence nabídek podle zákazníka',
        latencyCsvLabel: 'Stáhnout CSV latence',
        latencyEmpty: 'Latence budou zobrazeny po zpracování dalších nabídek.',
        latencyTruncated: 'Zobrazuje se omezený seznam zákazníků kvůli objemu.',
        latencyColumns: {
          buyer: 'Zákazník',
          rfqs: 'Poptávky',
          quotes: 'Nabídky',
          response: 'Odezva',
          timeToTable: 'Čas do tabulky',
          quoteLatency: 'Latence nabídky',
          rfqToQuoteLatency: 'RFQ→nabídka',
          lastSeen: 'Naposledy',
        },
        csvDisabled: 'Export CSV již brzy',
        csvEnabled: 'Stáhnout CSV',
      },
      readiness: {
        title: 'Připravenost',
        helperReady: 'Pracovní prostor splňuje požadavky pro automatické návrhy.',
        helperInProgress: 'Dokončete položky kontrolního seznamu pro zpřístupnění automatických návrhů.',
        helperInsufficient: 'Pro posouzení připravenosti potřebujeme více dat.',
        badgeReady: 'Připraveno',
        badgeInProgress: 'Probíhá',
        helpLabel: 'Jak funguje připravenost',
        checklistHeading: 'Kontrolní seznam připravenosti',
        checklistEmpty: 'Pro vyhodnocení připravenosti chybí další signály.',
        currentLabel: 'Aktuálně: {value}',
        targetLabel: 'Cíl: {value}',
        samplesLabel: 'Vzorky',
        autoDraftHeading: 'Způsobilost pro automatické návrhy',
        autoDraftEligible: 'Automatické návrhy jsou pro tento workspace aktivní.',
        autoDraftIneligible: 'Automatické návrhy se zpřístupní po dokončení kontrolního seznamu.',
        bottlenecksHeading: 'Hlavní úzká místa',
        bottlenecksEmpty: 'V tomto okně nebyla zjištěna žádná úzká místa.',
        csvChecklistLabel: 'Stáhnout CSV kontrolního seznamu',
        csvBottlenecksLabel: 'Stáhnout CSV úzkých míst',
        tooltipTitle: 'O připravenosti',
        tooltipBody: 'Připravenost zvýrazňuje požadavky pro automatické návrhy a ukazuje zbývající mezery.',
        tooltipDismiss: 'Zavřít',
        latencyChipTemplate: '{value} · cíl {target}',
        fieldChipTemplate: '{value} · cíl {target} · chybí {missing}',
        csvHeaders: {
          checklist: {
            item: 'Kontrolní položka',
            status: 'Stav',
            current: 'Aktuální hodnota',
            target: 'Cílová hodnota',
            samples: 'Vzorky',
            unit: 'Jednotka',
          },
          bottlenecks: {
            label: 'Úzké místo',
            metric: 'Metrika',
            category: 'Kategorie',
            severity: 'Závažnost',
            samples: 'Vzorky',
            value: 'Aktuální hodnota',
            target: 'Cílová hodnota',
            missing: 'Chybějící',
            total: 'Celkem',
          },
        },
        csvStatusLabels: {
          ready: 'Připraveno',
          inProgress: 'Probíhá',
        },
        csvUnitLabels: {
          percent: 'Procenta',
          count: 'Počet',
        },
        csvCategoryLabels: {
          latency: 'Latence',
          field: 'Pole',
        },
        csvSeverityLabels: {
          critical: 'Kritické',
          warning: 'Varování',
          info: 'Informativní',
        },
      },
    },
    de: {
      title: 'Arbeitsbereichsstatistiken',
      updated: 'Aktualisiert {time}',
      refresh: 'Aktualisieren',
      timeWindows: {
        '24h': '24 Std.',
        '7d': '7 Tage',
        '30d': '30 Tage',
      },
      tabs: {
        performance: 'Performance',
        overview: 'Übersicht',
        latency: 'Latenz',
        buyers: 'Käufer & Materialien',
        quality: 'Qualität',
        readiness: 'Bereitschaft',
      },
      tier2Tabs: {
        dataQuality: 'Datenqualität',
        teamActivity: 'Teamaktivität',
      },
      kpiPlaceholderTitle: 'Bald verfügbare Kennzahlen',
      kpiPlaceholderBody: 'Für diese Stufe bereiten wir erweiterte Einblicke vor.',
      kpiRegionLabel: 'Wichtigste Kennzahlen des Arbeitsbereichs',
      emptyMessage: 'Arbeitsbereichsstatistiken werden in Kürze angezeigt.',
      kpiLabels: {
        rfqsProcessed: 'Verarbeitete RFQs',
        quotesSent: 'Versandte Angebote',
        responseRatio: 'Antwortquote',
        winRatio: 'Gewinnquote',
        quotesWon: 'Gewonnen',
        quotesLost: 'Verloren',
        quotesPending: 'Ausgang ausstehend',
        medianQuoteTurnaround: 'Median der Angebotsdauer',
        medianFirstResponseTime: 'Median bis zur ersten Antwort',
        avgCompleteness: 'Durchschn. Vollständigkeit',
      },
      csvButtons: {
        summary: 'CSV-Zusammenfassung herunterladen',
        detail: 'CSV-Details herunterladen',
      },
      attachmentsCard: {
        title: 'Anhangsmix',
        helperEmpty: 'In diesem Zeitraum wurden keine Anhänge verarbeitet.',
        helperInsufficient: 'Für die Visualisierung werden mehr Anhangstypen benötigt.',
        otherLabel: 'Sonstige',
        legendTemplate: '{label} · {percent} ({count})',
        labels: {
          pdf: 'PDF',
          docx: 'DOCX',
          xlsx: 'XLSX',
          csv: 'CSV',
          cad: 'CAD',
          zip: 'ZIP',
        },
      },
      notesCard: {
        title: 'Notizen & Rückfragen',
        notesLabel: 'Geteilte Notizen',
        notes: 'Geteilte Notizen zu {filled} von {total} RFQs',
        clarifyingLabel: 'Rückfragen',
        clarifying: 'Es wurden {count} Rückfragen vorgeschlagen',
        clarifyingNeeded: 'Erforderlich bei {needed} RFQs',
        clarifyingNotNeeded: 'Nicht erforderlich bei {notNeeded} RFQs',
      },
      performance: {
        heading: 'Performance-Einblicke',
        csvDisabled: 'CSV-Export folgt in Kürze',
        rfqsQuotes: {
          heading: 'Anfragen vs. Angebote',
          csvLabel: 'CSV Anfragen vs. Angebote herunterladen',
          helperEmpty: 'Trends zu Anfragen und Angeboten erscheinen, sobald Daten vorliegen.',
          helperInsufficient: 'Für dieses Diagramm wird mehr Aktivität bei Anfragen und Angeboten benötigt.',
          placeholderTitle: 'Diagramm Anfragen vs. Angebote folgt in Kürze',
          placeholderBody: 'Wir bereiten dieses Diagramm zur Visualisierung von Anfragen und Angeboten vor.',
          chartAria: 'Anfragen im Vergleich zu Angeboten im Zeitverlauf',
        },
        latencyTrend: {
          heading: 'Angebotslatenz-Trend',
          csvLabel: 'CSV zur Latenz herunterladen',
          helperEmpty: 'Latenztrends erscheinen, sobald genügend Anfragen angeboten wurden.',
          helperInsufficient: 'Für dieses Diagramm werden mehr konsistente Latenzwerte benötigt.',
          placeholderTitle: 'Latenzdiagramm folgt in Kürze',
          placeholderBody: 'Wir bereiten diese Visualisierung der Latenztrends vor.',
          chartAria: 'Trend der Angebotslatenz',
        },
        cacheHit: {
          heading: 'Cache-Effizienz',
          csvLabel: 'CSV zum Cache herunterladen',
          helperEmpty: 'Cache-Einblicke erscheinen nach weiterer Aktivität.',
          helperInsufficient: 'Für detaillierte Einblicke werden mehr Cache-Ereignisse benötigt.',
          placeholderTitle: 'Cache-Einblicke folgen in Kürze',
          placeholderBody: 'Wir bereiten Zusammenfassungen von Cache-Treffern und -Fehlversuchen vor.',
          chartAria: 'Zusammenfassung der Cache-Treffer',
        },
        csvHeaders: {
          rfqsQuotes: {
            granularity: 'Granularität',
            bucketStart: 'Beginn des Zeitfensters',
            rfqs: 'Anfragen',
            quotes: 'Angebote',
          },
          latencyTrend: {
            recordedAt: 'Erfasst am',
            quoteLatency: 'Angebotslatenz',
            rfqToQuoteLatency: 'RFQ→Angebotslatenz',
            timeToTable: 'Zeit bis zur Tabelle',
            extractionLatency: 'Extraktionslatenz',
          },
          cacheHit: {
            section: 'Bereich',
            label: 'Bezeichnung',
            value: 'Wert',
            summarySection: 'Zusammenfassung',
            timelineSection: 'Zeitachse',
            hits: 'Treffer',
            misses: 'Fehlversuche',
            hitRate: 'Trefferquote',
          },
        },
        cacheHitStatus: {
          hit: 'Treffer',
          miss: 'Fehlschlag',
        },
      },
      dataQuality: {
        title: 'Datenqualität',
        csvLabel: 'Datenqualität als CSV herunterladen',
        completenessHeading: 'Feldabdeckung',
        completenessEmpty: 'Für die Darstellung werden weitere Anfragen benötigt.',
        missingHeading: 'Meist fehlende Felder',
        missingEmpty: 'Alles bestens – keine auffälligen fehlenden Felder.',
        attachmentsHeading: 'Anhangsmix',
        attachmentsEmpty: 'Für dieses Zeitfenster liegt kein Anhangsmix vor.',
        buyersHeading: 'Käufermix',
        buyersEmpty: 'Käufer-Einblicke erscheinen nach weiterer Aktivität.',
        readinessTitle: 'Angebotsbereitschaft',
        readinessSubtitle: 'Prozent der aktuellen RFQs mit erfüllten Schlüsselfeldern',
        readinessHelperTemplate: 'Bereit: {ready} von {total} RFQs. Wichtigste Lücken: {fields}.',
        readinessHelperNoGaps: 'Alle Schlüsselfelder sind abgedeckt.',
        readinessHelperEmpty: 'Für die Bewertung der Angebotsbereitschaft werden mehr RFQs benötigt.',
        readinessTooltipLabel: 'Schlüsselfelder der Bereitschaft anzeigen',
        readinessTooltipEmpty: 'Schlüsselfelder erscheinen, sobald Bereitschaftsdaten verfügbar sind.',
        readinessValueEmpty: '--',
      },
      teamActivity: {
        title: 'Teamaktivität',
        csvLabel: 'Teamaktivität als CSV herunterladen',
        leaderboardHeading: 'Aktivste Teammitglieder',
        leaderboardEmpty: 'Die Teamaktivität erscheint, sobald das Team RFQs bearbeitet.',
        leaderboardRfqsLabel: 'Bearbeitete RFQs',
        leaderboardNotesLabel: 'Geteilte Notizen',
        leaderboardQuotesLabel: 'Gesendete Angebote',
        leaderboardResponseLabel: 'Antwortquote',
        clarificationsHeading: 'Rückfragen',
        clarificationsNeededLabel: 'Benötigt',
        clarificationsNotNeededLabel: 'Nicht benötigt',
        clarificationsQuestionsLabel: 'Fragen',
        clarificationsEmpty: 'Klärungsaktivitäten werden nach mehr Vorgängen angezeigt.',
        sparklineHeading: 'Aktuelle RFQ-Aktivität',
        sparklineEmpty: 'Der Aktivitätsverlauf erscheint nach weiteren RFQs.',
        sparklineRfqsLabel: 'RFQs',
        sparklineNotesLabel: 'Notizen',
      },
      buyersMaterials: {
        heading: 'Käufer & Materialien',
        helper: 'Beobachten Sie Käuferbindung, Materialmix und Latenzleistung.',
        buyersHeading: 'Top-Käufer',
        buyersCsvLabel: 'CSV der Käufer herunterladen',
        buyersEmpty: 'Käufer-Einblicke erscheinen nach weiterer Aktivität.',
        buyersTruncated: 'Aufgrund des Volumens werden nur Top-Käufer angezeigt.',
        rfqsLabel: 'Anfragen',
        quotesLabel: 'Angebote',
        trendUpLabel: 'Steigender Trend',
        trendDownLabel: 'Sinkender Trend',
        trendFlatLabel: 'Stabile Performance',
        anonymousBuyerFallback: 'Käufer',
        anonymousBuyerTemplate: 'Käufer · {hash}',
        materialsHeading: 'Materialmix',
        materialsCsvLabel: 'CSV des Materialmixes herunterladen',
        materialsEmpty: 'Der Materialmix erscheint nach weiterer Aktivität.',
        materialsTruncated: 'Aufgrund des Volumens werden nur Top-Materialien angezeigt.',
        materialRfqsLabel: 'Anfragen',
        materialQtyLabel: 'Gesamtmenge',
        materialFallback: 'Material',
        latencyHeading: 'Angebotslatenz nach Käufer',
        latencyCsvLabel: 'CSV der Latenz herunterladen',
        latencyEmpty: 'Latenz-Einblicke erscheinen nach genügend Angeboten.',
        latencyTruncated: 'Aufgrund des Volumens werden nur Top-Käufer angezeigt.',
        latencyColumns: {
          buyer: 'Käufer',
          rfqs: 'Anfragen',
          quotes: 'Angebote',
          response: 'Antwort',
          timeToTable: 'Zeit bis zur Tabelle',
          quoteLatency: 'Angebotslatenz',
          rfqToQuoteLatency: 'RFQ→Angebot',
          lastSeen: 'Zuletzt gesehen',
        },
        csvDisabled: 'CSV-Export folgt in Kürze',
        csvEnabled: 'CSV herunterladen',
      },
      readiness: {
        title: 'Bereitschaft',
        helperReady: 'Der Arbeitsbereich erfüllt die Anforderungen für automatische Entwürfe.',
        helperInProgress: 'Schließen Sie die Checklistenelemente ab, um automatische Entwürfe freizuschalten.',
        helperInsufficient: 'Für eine Bewertung der Bereitschaft werden mehr Daten benötigt.',
        badgeReady: 'Bereit',
        badgeInProgress: 'In Arbeit',
        helpLabel: 'So funktioniert Bereitschaft',
        checklistHeading: 'Bereitschafts-Checkliste',
        checklistEmpty: 'Für die Bewertung der Bereitschaft fehlen noch Signale.',
        currentLabel: 'Aktuell: {value}',
        targetLabel: 'Ziel: {value}',
        samplesLabel: 'Stichproben',
        autoDraftHeading: 'Eignung für Auto-Entwürfe',
        autoDraftEligible: 'Auto-Entwürfe sind für diesen Arbeitsbereich aktiviert.',
        autoDraftIneligible: 'Auto-Entwürfe werden nach Abschluss der Checkliste freigeschaltet.',
        bottlenecksHeading: 'Wichtigste Engpässe',
        bottlenecksEmpty: 'Für dieses Zeitfenster wurden keine Engpässe gefunden.',
        csvChecklistLabel: 'Checkliste als CSV herunterladen',
        csvBottlenecksLabel: 'Engpässe als CSV herunterladen',
        tooltipTitle: 'Über die Bereitschaft',
        tooltipBody: 'Die Bereitschaft zeigt die Anforderungen für Auto-Entwürfe und verbleibende Lücken an.',
        tooltipDismiss: 'Schließen',
        latencyChipTemplate: '{value} · Ziel {target}',
        fieldChipTemplate: '{value} · Ziel {target} · {missing} fehlen',
        csvHeaders: {
          checklist: {
            item: 'Checklistenpunkt',
            status: 'Status',
            current: 'Aktueller Wert',
            target: 'Zielwert',
            samples: 'Stichproben',
            unit: 'Einheit',
          },
          bottlenecks: {
            label: 'Engpass',
            metric: 'Metrik',
            category: 'Kategorie',
            severity: 'Schweregrad',
            samples: 'Stichproben',
            value: 'Aktueller Wert',
            target: 'Zielwert',
            missing: 'Fehlend',
            total: 'Gesamt',
          },
        },
        csvStatusLabels: {
          ready: 'Bereit',
          inProgress: 'In Arbeit',
        },
        csvUnitLabels: {
          percent: 'Prozent',
          count: 'Anzahl',
        },
        csvCategoryLabels: {
          latency: 'Latenz',
          field: 'Feldabdeckung',
        },
        csvSeverityLabels: {
          critical: 'Kritisch',
          warning: 'Warnung',
          info: 'Info',
        },
      },
    },
  };

  app.strings = {
    ...existingStrings,
    ui: {
      ...UI_COPY,
      ...(existingStrings.ui || {}),
    },
    rfqTable: {
      ...RFQ_TABLE_COPY,
      ...(existingStrings.rfqTable || {}),
    },
    statsTile: {
      ...STATS_TILE_COPY,
      ...(existingStrings.statsTile || {}),
    },
  };
})(typeof globalThis !== 'undefined' ? globalThis : this);
